import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, Polygon, useMap, useMapEvents, LayersControl, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { Plus, Eye, EyeSlash, MapPin, Target, Ruler, House, Warning, Funnel, DownloadSimple, ArrowsClockwise, Path, Stack, ChartBar, Crosshair, MagnifyingGlass, UserCircle, CheckCircle, XCircle, NavigationArrow } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const YANGON = [16.8409, 96.1235];
const MAP_CACHE_DB = 'myanmarlink-fttx-map-cache';
const MAP_CACHE_STORE = 'layers';

const normalizeMapSearchValue = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '')
  .replace(/\d+/g, digits => String(Number(digits)));

const openMapCache = () => new Promise(resolve => {
  if (!window.indexedDB) { resolve(null); return; }
  const request = window.indexedDB.open(MAP_CACHE_DB, 1);
  request.onupgradeneeded = () => request.result.createObjectStore(MAP_CACHE_STORE);
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => resolve(null);
});

const readMapCache = async (key, ttl) => {
  const db = await openMapCache();
  if (!db) return null;
  return new Promise(resolve => {
    const request = db.transaction(MAP_CACHE_STORE, 'readonly').objectStore(MAP_CACHE_STORE).get(key);
    request.onsuccess = () => {
      const entry = request.result;
      resolve(entry && Date.now() - entry.ts <= ttl ? entry.data : null);
    };
    request.onerror = () => resolve(null);
  });
};

const writeMapCache = async (key, data) => {
  const db = await openMapCache();
  if (!db) return;
  await new Promise(resolve => {
    const request = db.transaction(MAP_CACHE_STORE, 'readwrite').objectStore(MAP_CACHE_STORE).put({ ts: Date.now(), data }, key);
    request.onsuccess = request.onerror = () => resolve();
  });
};

const clearMapCache = async () => {
  const db = await openMapCache();
  if (!db) return;
  await new Promise(resolve => {
    const request = db.transaction(MAP_CACHE_STORE, 'readwrite').objectStore(MAP_CACHE_STORE).clear();
    request.onsuccess = request.onerror = () => resolve();
  });
};

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

// ── Icon helpers ──────────────────────────────────────────────────────────────
const makeIcon = (color, size = 24, label = '') => L.divIcon({
  className: '',
  html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:${Math.max(8, size/3)}px;font-weight:bold;">${label}</div>`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2]
});

const makeSquareIcon = (color, size = 22, label = '') => L.divIcon({
  className: '',
  html: `<div style="width:${size}px;height:${size}px;border-radius:4px;background:${color};border:2.5px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:${Math.max(7, size/3.5)}px;font-weight:bold;">${label}</div>`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2]
});

// Diamond icon for splitters
const makeDiamondIcon = (color, size = 18, label = '') => L.divIcon({
  className: '',
  html: `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);transform:rotate(45deg);display:flex;align-items:center;justify-content:center;"></div>`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size / 2]
});

// House icon for customers
const makeHouseIcon = (color, size = 16) => L.divIcon({
  className: '',
  html: `<div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4))">
      <path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"/>
    </svg>
  </div>`,
  iconSize: [size, size],
  iconAnchor: [size / 2, size]
});

// ODB icon: green is only a recorded online status, red is only a recorded down/offline status, and unknown is neutral gray.
const makeOdbIcon = (status, size = 28) => {
  const normalizedStatus = String(status || 'unknown').trim().toLowerCase();
  const isOnline = normalizedStatus === 'online' || normalizedStatus === 'active';
  const isDown = ['offline', 'down', 'inactive'].includes(normalizedStatus);
  const color = isOnline ? '#16A34A' : isDown ? '#DC2626' : '#64748B';
  const borderColor = isOnline ? '#4ADE80' : isDown ? '#F87171' : '#94A3B8';
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:4px;background:${color};border:3px solid white;box-shadow:0 0 0 2px ${borderColor},0 3px 10px rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;color:white;font-size:${Math.max(7, size/3.8)}px;font-weight:900;letter-spacing:-0.5px;">ODB</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};
const makeSearchHighlightIcon = (objType) => L.divIcon({
  className: '',
  html: `<div style="width:38px;height:38px;border-radius:50%;border:4px solid #FBBF24;background:rgba(251,191,36,0.28);box-shadow:0 0 0 7px rgba(251,191,36,0.28),0 0 18px rgba(245,158,11,0.9);display:flex;align-items:center;justify-content:center;color:#78350F;font-size:8px;font-weight:900;">${objType === 'odb' ? 'ODB' : objType === 'olt' ? 'OLT' : objType === 'customer' ? 'CUS' : 'SP'}</div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19]
});
const ICONS = {
  olt: makeSquareIcon('#BA1A1A', 26, 'OLT'),
  odb: makeOdbIcon('unknown', 28),
  odb_online: makeOdbIcon('online', 28),
  odb_offline: makeOdbIcon('offline', 28),
  splitter: makeDiamondIcon('#EAB308', 18, ''),
  customer: makeHouseIcon('#2563EB', 14),
  potential: makeIcon('#F57F17', 12, ''),
};

const odbStatusValue = (object) => String(object?.status || object?.alarm_status || 'unknown').trim().toLowerCase() || 'unknown';
const odbStatusLabel = (status) => status === 'unknown' ? 'Unknown' : String(status).replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const CABLE_COLORS = {
  backbone_route: '#EF4444',
  distribution_route: '#3B82F6',
  drop_cable: '#22C55E',
};

const CABLE_STATUS_COLORS = {
  Active: null,       // use default cable color
  Cut: '#FF0000',     // bright red override
  Degraded: '#FF8C00', // orange override
};

const ZONE_COLORS = { green: '#006D3B', yellow: '#F57F17', red: '#BA1A1A' };

const dmaCustIcon = (enableuser, expiration, lastSession) => {
  const now = new Date();
  const expDate = expiration && expiration !== '0000-00-00T00:00:00' && expiration !== '0000-00-00 00:00:00' ? new Date(expiration) : null;
  const isExpired = expDate && expDate <= now;
  const isActive = enableuser === 1 && !isExpired;
  
  let color;
  if (!isActive) {
    color = '#EF4444'; // Red: no service or expired
  } else {
    const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
    const lastUse = lastSession ? new Date(lastSession) : null;
    if (!lastUse || lastUse < thirtyDaysAgo) {
      color = '#374151'; // Dark gray: active but long inactive
    } else {
      color = '#22C55E'; // Green: active and recently used
    }
  }
  
  // House icon for DMA customers: blue=active, grey=inactive, orange=expired
  const houseColor = !isActive ? '#F97316' : (color === '#374151' ? '#6B7280' : '#2563EB');
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:14px;height:14px;display:flex;align-items:center;justify-content:center;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="${houseColor}" stroke="white" stroke-width="1.5" style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4))">
        <path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"/>
      </svg>
    </div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 14]
  });
};
const tracedCustIcon = () => L.divIcon({
  className: '',
  html: `<div style="width:16px;height:16px;border-radius:50%;background:#F59E0B;border:3px solid white;box-shadow:0 0 0 2px #F59E0B;"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

// ── Map event handlers ────────────────────────────────────────────────────────
function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

function UnifiedSearchPopup({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (!focus || !Number.isFinite(Number(focus.lat)) || !Number.isFinite(Number(focus.lng))) return;
    const popup = L.popup({ autoPan: true, closeButton: true, maxWidth: 260 })
      .setLatLng([Number(focus.lat), Number(focus.lng)])
      .setContent(`<div style="font:12px/1.4 sans-serif;min-width:180px"><strong>${String(focus.name || focus.object_id || focus.username || 'Map result').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char])}</strong><br/><span style="color:#64748b;font-size:10px">${String(focus.label || focus.obj_type || 'Map result').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char])}</span>${focus.description ? `<br/><span style="font-size:10px">${String(focus.description).replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char])}</span>` : ''}</div>`)
      .openOn(map);
    return () => { if (map._popup === popup) map.closePopup(popup); };
  }, [focus, map]);
  return null;
}

// ── Cable length calculation ──────────────────────────────────────────────────
function calcCableLength(points) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const [lat1, lng1] = points[i - 1];
    const [lat2, lng2] = points[i];
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return Math.round(total);
}

function formatLength(meters) {
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${meters} m`;
}

// ── Alarm badge ───────────────────────────────────────────────────────────────
function AlarmBadge({ status }) {
  const colors = { Normal: 'bg-green-100 text-green-700', Warning: 'bg-yellow-100 text-yellow-700', Critical: 'bg-red-100 text-red-700' };
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${colors[status] || colors.Normal}`}>{status || 'Normal'}</span>;
}

// ── Port utilization bar ──────────────────────────────────────────────────────
function PortBar({ used, total, label }) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  const color = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#3B82F6';
  return (
    <div>
      {label && <div className="flex justify-between text-[10px] mb-0.5"><span>{label}</span><span className="font-bold tabular-nums">{used}/{total} ({pct.toFixed(0)}%)</span></div>}
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function FiberMap() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const isBMFTSup = user?.role === 'BMFT Supervisor';
  const canEdit = isAdmin || isBMFTSup;

  // ── URL params (focus on customer location) ────────────────────────────
  const location = useLocation();
  const focusParam = new URLSearchParams(location.search).get('focus');
  const focusUsername = new URLSearchParams(location.search).get('username');
  const focusObjectId = new URLSearchParams(location.search).get('object');
  const globalSearchTool = new URLSearchParams(location.search).get('search_tool');
  const globalSearchQuery = new URLSearchParams(location.search).get('q') || '';
  const focusCoords = useMemo(() => focusParam ? focusParam.split(',').map(Number) : null, [focusParam]);
  // ── State ──────────────────────────────────────────────────────────────────
  const [objects, setObjects] = useState([]);
  const [downAlerts, setDownAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [miniDash, setMiniDash] = useState({});
  const [coverageZones, setCoverageZones] = useState([]);
  const [layers, setLayers] = useState({
    backbone_route: true, distribution_route: true, drop_cable: true,
    odb: true, olt: true, splitter: true, customer: true, potential: true,
    coverage_zones: true,
  });
  const [addOpen, setAddOpen] = useState(false);
  const [clickPos, setClickPos] = useState(null);
  const [addType, setAddType] = useState('odb');
  const [addForm, setAddForm] = useState({
    name: '', capacity: 16, used_ports: 0, description: '',
    house_no: '', street: '', township: '', estimated_houses: 0,
    ratio: '1:8', alarm_status: 'Normal', parent_id: '', port_label: ''
  });
  const [distResult, setDistResult] = useState(null);
  const [drawMode, setDrawMode] = useState(null); // 'backbone_route' | 'distribution_route' | 'drop_cable' | 'coverage_zone'
  const [drawPoints, setDrawPoints] = useState([]);
  const [drawCableForm, setDrawCableForm] = useState({ name: '', cable_status: 'Active', length_meters: '' });
  const [importing, setImporting] = useState(false);
  const [kmzLayers, setKmzLayers] = useState([]);
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [editLayer, setEditLayer] = useState(null);
  const [editLayerName, setEditLayerName] = useState('');
  // ODB Port Management
  const [portOpen, setPortOpen] = useState(null);
  const [portData, setPortData] = useState(null);
  const [portForm, setPortForm] = useState({ port_number: '', customer_id: '', customer_name: '', customer_phone: '', customer_address: '', service_plan: '', onu_serial: '' });
  // DMA Customers
  const [dmaCustomers, setDmaCustomers] = useState([]);
  const [showDmaCustomers, setShowDmaCustomers] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  const [dmaFilter, setDmaFilter] = useState('all');
  const [odbStatusFilter, setOdbStatusFilter] = useState('all');
  const [odbPopupDetails, setOdbPopupDetails] = useState({});
  const [odbConnections, setOdbConnections] = useState({});
  const [selectedOdbConnectionId, setSelectedOdbConnectionId] = useState(null);
  const [highlightedObjectId, setHighlightedObjectId] = useState(null);
  const [highlightedCustomerId, setHighlightedCustomerId] = useState(null);
  const [unifiedSearchFocus, setUnifiedSearchFocus] = useState(null);
  // Path tracing
  const [tracedPath, setTracedPath] = useState(null);
  const [tracingId, setTracingId] = useState('');
  const [traceLoading, setTraceLoading] = useState(false);
  // Hierarchy popup
  const [hierarchyData, setHierarchyData] = useState(null);
  const [hierarchyOpen, setHierarchyOpen] = useState(false);
  // OLT/Splitter detail popup
  const [detailOpen, setDetailOpen] = useState(null);
  const [detailData, setDetailData] = useState(null);
  // Cable edit
  const [cableEditOpen, setCableEditOpen] = useState(null);
  const [cableEditForm, setCableEditForm] = useState({ name: '', cable_status: 'Active', length_meters: '' });
  // Coverage zone draw
  const [zoneForm, setZoneForm] = useState({ name: '', capacity: 100, potential_customers: 0 });
  // Coverage Check
  const [coverageAddress, setCoverageAddress] = useState('');
  const [coverageCoords, setCoverageCoords] = useState(null);
  const [coverageResult, setCoverageResult] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageMarker, setCoverageMarker] = useState(null);
  const [coverageRadius, setCoverageRadius] = useState(null);
  const [routeData, setRouteData] = useState(null);       // { coords, distance, duration, steps, mode }
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeMode, setRouteMode] = useState('driving');  // 'driving' | 'walking'
  const [showDirections, setShowDirections] = useState(false);
  const [custLookupMode, setCustLookupMode] = useState(false);
  const [custLookupQ, setCustLookupQ] = useState('');
  const [custLookupResults, setCustLookupResults] = useState([]);
  const [custLookupLoading, setCustLookupLoading] = useState(false);
  const [coverageGeoLoading, setCoverageGeoLoading] = useState(false);

  // ── Smart caching & progressive loading state ──────────────────────────────
  const [layerStatus, setLayerStatus] = useState({
    objects: 'idle',    // 'idle' | 'loading' | 'loaded' | 'failed'
    customers: 'idle',
    routes: 'idle',
    stats: 'idle',
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const CACHE_TTL = 30 * 60 * 1000; // 30 minutes in ms
  const autoRefreshRef = useRef(null);
  const [coverageSuggestions, setCoverageSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const coverageSuggestTimer = React.useRef(null);
  const globalSearchHandoffRef = React.useRef('');
  // UI
  const [mapRef, setMapRef] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('layers'); // 'layers' | 'tools' | 'trace'

  // ── Coverage Check Handlers ───────────────────────────────────────────────
  const handleCoverageGPS = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setCoverageGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoverageCoords(coords);
        setCoverageMarker(coords);
        setCoverageRadius(300);
        if (mapRef) mapRef.flyTo([coords.lat, coords.lng], 17);
        setCoverageGeoLoading(false);
        setCoverageSuggestions([]);
        setShowSuggestions(false);
        toast.success('📍 Location detected');
        setTimeout(() => doCheckCoverage(coords), 400);
      },
      err => {
        const msg = err.code === 1 ? 'Location permission denied' : err.code === 2 ? 'Position unavailable' : 'GPS timeout';
        toast.error('GPS: ' + msg);
        setCoverageGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) { setCoverageSuggestions([]); setShowSuggestions(false); return; }
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&countrycodes=MM&addressdetails=1&accept-language=en,my`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en,my', 'User-Agent': 'MyanmarLinkERP/1.0' } });
      const data = await res.json();
      if (data && data.length > 0) {
        setCoverageSuggestions(data);
        setShowSuggestions(true);
      } else {
        const res2 = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ' Myanmar')}&format=json&limit=5&addressdetails=1`, { headers: { 'User-Agent': 'MyanmarLinkERP/1.0' } });
        const data2 = await res2.json();
        setCoverageSuggestions(data2 || []);
        setShowSuggestions((data2 || []).length > 0);
      }
    } catch (e) {
      setCoverageSuggestions([]);
    }
  };

  const handleCoverageAddressChange = (val) => {
    setCoverageAddress(val);
    if (coverageSuggestTimer.current) clearTimeout(coverageSuggestTimer.current);
    coverageSuggestTimer.current = setTimeout(() => fetchSuggestions(val), 350);
  };

  const handleSuggestionSelect = (item) => {
    const coords = { lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
    setCoverageAddress(item.display_name.split(',').slice(0, 2).join(','));
    setCoverageCoords(coords);
    setCoverageMarker(coords);
    setCoverageRadius(300);
    setCoverageSuggestions([]);
    setShowSuggestions(false);
    if (mapRef) mapRef.flyTo([coords.lat, coords.lng], 17);
    toast.success('📍 ' + item.display_name.substring(0, 60));
    setTimeout(() => doCheckCoverage(coords), 400);
  };

  const handleCoverageGeocode = async (queryOverride = coverageAddress) => {
    const query = String(queryOverride || '').trim();
    if (!query) return;
    setCoverageGeoLoading(true);
    setShowSuggestions(false);
    try {
      let url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=MM`;
      let res = await fetch(url, { headers: { 'User-Agent': 'MyanmarLinkERP/1.0', 'Accept-Language': 'en,my' } });
      let data = await res.json();
      if (!data || data.length === 0) {
        url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ' Myanmar')}&format=json&limit=1`;
        res = await fetch(url, { headers: { 'User-Agent': 'MyanmarLinkERP/1.0' } });
        data = await res.json();
      }
      if (data && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        setCoverageCoords(coords);
        setCoverageMarker(coords);
        setCoverageRadius(300);
        if (mapRef) mapRef.flyTo([coords.lat, coords.lng], 17);
        toast.success('📍 Found: ' + data[0].display_name.substring(0, 60));
        setTimeout(() => doCheckCoverage(coords), 400);
      } else {
        toast.error('Address not found. Try a more specific address.');
      }
    } catch (e) {
      toast.error('Geocoding failed — check internet connection');
    }
    setCoverageGeoLoading(false);
  };

  const handleGetDirections = async (mode = routeMode) => {
    if (!coverageCoords || !coverageResult?.nearest_odb) return;
    const odb = coverageResult.nearest_odb;
    if (!odb.lat || !odb.lng) { toast.error('ODB Box location not available'); return; }
    setRouteLoading(true);
    setRouteData(null);
    try {
      const profile = mode === 'walking' ? 'foot' : 'driving';
      const url = `https://router.project-osrm.org/route/v1/${profile}/${coverageCoords.lng},${coverageCoords.lat};${odb.lng},${odb.lat}?overview=full&geometries=geojson&steps=true`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.code === 'Ok' && data.routes?.length > 0) {
        const route = data.routes[0];
        const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        const steps = route.legs?.[0]?.steps?.map(s => ({
          instruction: s.maneuver?.type || 'Continue',
          name: s.name || '',
          distance: s.distance,
          duration: s.duration,
          type: s.maneuver?.type,
          modifier: s.maneuver?.modifier,
        })) || [];
        setRouteData({
          coords,
          distance: route.distance,
          duration: route.duration,
          steps,
          mode,
          odbName: odb.name,
          odbLat: odb.lat,
          odbLng: odb.lng,
        });
        setShowDirections(true);
        // Fit map bounds to show both points
        if (mapRef) {
          const bounds = [
            [coverageCoords.lat, coverageCoords.lng],
            [odb.lat, odb.lng],
          ];
          mapRef.fitBounds(bounds, { padding: [60, 60] });
        }
        const distKm = (route.distance / 1000).toFixed(1);
        const mins = Math.round(route.duration / 60);
        toast.success(`🧭 Route: ${distKm} km · ~${mins} min (${mode})`);
      } else {
        toast.error('Could not find a route to this ODB Box');
      }
    } catch (e) {
      toast.error('Directions unavailable — check internet connection');
    }
    setRouteLoading(false);
  };

  const clearRoute = () => {
    setRouteData(null);
    setShowDirections(false);
  };

  const formatStepInstruction = (step) => {
    const icons = {
      'turn': step.modifier === 'left' ? '↰' : step.modifier === 'right' ? '↱' : '↑',
      'new name': '↑', 'depart': '📍', 'arrive': '🏁',
      'roundabout': '🔄', 'rotary': '🔄', 'fork': '⑂',
      'merge': '⤵', 'on ramp': '↗', 'off ramp': '↘',
      'end of road': '⊣', 'continue': '↑',
    };
    const icon = icons[step.type] || '↑';
    const dist = step.distance < 1000 ? `${Math.round(step.distance)}m` : `${(step.distance/1000).toFixed(1)}km`;
    return { icon, text: step.name || step.instruction, dist };
  };

    const doCheckCoverage = async (coords) => {
    if (!coords) return;
    clearRoute();
    setCoverageLoading(true);
    try {
      const res = await fetch(`${API}/fiber-map/coverage-check?lat=${coords.lat}&lng=${coords.lng}`);
      if (res.ok) {
        const data = await res.json();
        setCoverageResult(data);
        if (data.covered) {
          toast.success(`✅ Covered! Nearest ODB: ${data.nearest_odb?.name} (${data.distance_m}m)`);
        } else {
          toast.error(`❌ Not covered. Nearest ODB: ${data.nearest_odb?.name || 'None'} (${data.distance_m || '?'}m)`);
        }
      }
    } catch (e) { /* silent */ }
    setCoverageLoading(false);
  };


  const handleCheckCoverage = async () => {
    if (!coverageCoords) return;
    setCoverageRadius(300);
    await doCheckCoverage(coverageCoords);
  };

  const handleCustLookup = async (queryOverride = custLookupQ) => {
    const query = String(queryOverride || '').trim();
    if (!query) return;
    setCustLookupLoading(true);
    try {
      const res = await fetch(`${API}/fiber-map/customer-location-search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        const results = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setCustLookupResults(results);
        if (results.length === 0) toast('No customers found');
      } else {
        toast.error('Customer lookup failed');
      }
    } catch (e) {
      toast.error('Lookup error');
    }
    setCustLookupLoading(false);
  };

  // ── Cache helpers (localStorage with TTL) ─────────────────────────────────
  const CACHE_KEYS = {
    objects: 'fttx_cache_objects',
    stats: 'fttx_cache_stats',
    layers: 'fttx_cache_layers',
    zones: 'fttx_cache_zones',
    customers: 'fttx_cache_customers',
  };
  const saveCache = async (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) { /* quota exceeded — ignore */ }
    await writeMapCache(key, data);
  };
  const loadCache = async (key) => {
    const indexed = await readMapCache(key, CACHE_TTL);
    if (indexed !== null) return indexed;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(key); return null; }
      return data;
    } catch (e) { return null; }
  };
  const clearAllCaches = async () => {
    Object.values(CACHE_KEYS).forEach(k => localStorage.removeItem(k));
    await clearMapCache();
  };

  // ── Load data ──────────────────────────────────────────────────────────────
  // ── Progressive load: Phase 1 (ODB/OLT/Splitters) → Phase 2 (Customers) → Phase 3 (Routes/Stats) ──
  const loadStaticData = useCallback(async (forceRefresh = false) => {
    // Phase 1: Load network objects (ODB, OLT, Splitters) — check cache first
    const cachedObjects = !forceRefresh ? await loadCache(CACHE_KEYS.objects) : null;
    if (cachedObjects) {
      setObjects(Array.isArray(cachedObjects) ? cachedObjects : []);
      setLayerStatus(s => ({ ...s, objects: 'loaded' }));
    } else {
      setLayerStatus(s => ({ ...s, objects: 'loading' }));
      try {
        const oRes = await fetch(`${API}/fiber-map/objects`);
        if (oRes.ok) {
          const d = await oRes.json();
          const arr = Array.isArray(d) ? d : [];
          setObjects(arr);
          saveCache(CACHE_KEYS.objects, arr);
          setLayerStatus(s => ({ ...s, objects: 'loaded' }));
        } else { setLayerStatus(s => ({ ...s, objects: 'failed' })); }
      } catch (e) { setLayerStatus(s => ({ ...s, objects: 'failed' })); }
    }

    // Active Down alerts are intentionally not cached: they are the live source for map DOWN status.
    try {
      const downRes = await fetch(`${API}/odb-active-downs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
      });
      if (downRes.ok) {
        const downData = await downRes.json();
        setDownAlerts(Array.isArray(downData) ? downData : []);
      } else {
        setDownAlerts([]);
      }
    } catch (e) {
      setDownAlerts([]);
    }

    // Phase 2: Load customers (after objects) — check cache first (5 min TTL)
    setLayerStatus(s => ({ ...s, customers: 'loading' }));
    try {
    const cachedCustomers = !forceRefresh ? await loadCache(CACHE_KEYS.customers) : null;
      if (cachedCustomers) {
        setDmaCustomers(Array.isArray(cachedCustomers) ? cachedCustomers : []);
        setLayerStatus(s => ({ ...s, customers: 'loaded' }));
      } else {
        const cRes = await fetch(`${API}/fiber-map/dma-customers`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` }
        });
        if (cRes.ok) {
          const d = await cRes.json();
          const arr = Array.isArray(d) ? d : [];
          setDmaCustomers(arr);
          saveCache(CACHE_KEYS.customers, arr);
          setLayerStatus(s => ({ ...s, customers: 'loaded' }));
        } else { setLayerStatus(s => ({ ...s, customers: 'failed' })); }
      }
    } catch (e) { setLayerStatus(s => ({ ...s, customers: 'failed' })); }

    // Phase 3: Load routes/layers and stats (last)
    const cachedLayers = !forceRefresh ? await loadCache(CACHE_KEYS.layers) : null;
    const cachedZones = !forceRefresh ? await loadCache(CACHE_KEYS.zones) : null;
    setLayerStatus(s => ({ ...s, routes: cachedLayers ? 'loaded' : 'loading', stats: 'loading' }));

    const phase3Promises = [];
    if (!cachedLayers) {
      phase3Promises.push(
        fetch(`${API}/fiber-map/layers`).then(async r => {
          if (r.ok) {
            const layerData = await r.json();
            setKmzLayers(layerData);
            const hidden = new Set();
            layerData.forEach(l => { if (!l.visible) hidden.add(l.layer_name); });
            setHiddenLayers(hidden);
            saveCache(CACHE_KEYS.layers, layerData);
            setLayerStatus(s => ({ ...s, routes: 'loaded' }));
          } else { setLayerStatus(s => ({ ...s, routes: 'failed' })); }
        }).catch(() => setLayerStatus(s => ({ ...s, routes: 'failed' })))
      );
    } else {
      setKmzLayers(cachedLayers);
      const hidden = new Set();
      cachedLayers.forEach(l => { if (!l.visible) hidden.add(l.layer_name); });
      setHiddenLayers(hidden);
    }
    if (!cachedZones) {
      phase3Promises.push(
        fetch(`${API}/fiber-map/coverage-zones`).then(async r => {
          if (r.ok) { const d = await r.json(); setCoverageZones(Array.isArray(d) ? d : []); saveCache(CACHE_KEYS.zones, Array.isArray(d) ? d : []); }
        }).catch(() => {})
      );
    } else {
      setCoverageZones(Array.isArray(cachedZones) ? cachedZones : []);
    }
    phase3Promises.push(
      fetch(`${API}/fiber-map/stats`).then(async r => {
        if (r.ok) { setStats(await r.json()); setLayerStatus(s => ({ ...s, stats: 'loaded' })); }
        else { setLayerStatus(s => ({ ...s, stats: 'failed' })); }
      }).catch(() => setLayerStatus(s => ({ ...s, stats: 'failed' })))
    );
    await Promise.all(phase3Promises);
    setLastUpdated(new Date());
  }, []);

  // Real-time refresh: only mini-dashboard + customer status (every 5 min)
  const loadRealtime = useCallback(async () => {
    try {
      const [mRes, cRes] = await Promise.all([
        fetch(`${API}/fiber-map/mini-dashboard`),
        fetch(`${API}/fiber-map/dma-customers`),
      ]);
      if (mRes.ok) setMiniDash(await mRes.json());
      if (cRes.ok) { const d = await cRes.json(); setDmaCustomers(Array.isArray(d) ? d : []); }
      setLastUpdated(new Date());
    } catch (e) { /* silent */ }
  }, []);

  const load = useCallback(async (forceRefresh = false) => {
    try { const mRes = await fetch(`${API}/fiber-map/mini-dashboard`); if (mRes.ok) setMiniDash(await mRes.json()); } catch (e) {}
    await loadStaticData(forceRefresh);
  }, [loadStaticData]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh real-time data every 5 minutes
  useEffect(() => {
    autoRefreshRef.current = setInterval(() => { loadRealtime(); }, 5 * 60 * 1000);
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [loadRealtime]);


  const focusNetworkObject = useCallback((object, zoom = 18) => {
    if (!object || !mapRef || !Number.isFinite(Number(object.lat)) || !Number.isFinite(Number(object.lng))) return;
    setLayers(current => ({ ...current, [object.obj_type]: true }));
    setHiddenLayers(current => {
      const next = new Set(current);
      next.delete(object.source_file || 'Manual');
      return next;
    });
    setHighlightedObjectId(object.object_id);
    setUnifiedSearchFocus({ ...object, label: object.obj_type === 'backbone_route' ? 'Backbone Fiber' : object.obj_type === 'distribution_route' ? 'Distribution Fiber' : object.obj_type === 'drop_cable' ? 'Drop Fiber' : String(object.obj_type || 'Network element').toUpperCase() });
    mapRef.flyTo([Number(object.lat), Number(object.lng)], zoom, { animate: true, duration: 0.7 });
    window.setTimeout(() => setHighlightedObjectId(id => id === object.object_id ? null : id), 3200);
  }, [mapRef]);

  const focusDmaCustomer = useCallback((customer, zoom = 18) => {
    const lat = Number(customer?.gpslat ?? customer?.lat);
    const lng = Number(customer?.gpslong ?? customer?.lng);
    if (!customer || !mapRef || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setLayers(current => ({ ...current, customer: true }));
    setShowDmaCustomers(true);
    setHighlightedCustomerId(customer.username);
    setUnifiedSearchFocus({ ...customer, lat, lng, name: `${customer.firstname || ''} ${customer.lastname || ''}`.trim() || customer.name || customer.username, label: 'DMA Customer', description: customer.address || customer.service_name || '' });
    mapRef.flyTo([lat, lng], zoom, { animate: true, duration: 0.7 });
    window.setTimeout(() => setHighlightedCustomerId(id => id === customer.username ? null : id), 3200);
  }, [mapRef]);

  const focusDownAlert = useCallback((object, zoom = 18) => {
    if (!object || !mapRef || !Number.isFinite(Number(object.lat)) || !Number.isFinite(Number(object.lng))) return;
    setLayers(current => ({ ...current, odb: true }));
    setOdbStatusFilter('down');
    setHighlightedObjectId(object.object_id);
    const alert = object.down_alert || {};
    setUnifiedSearchFocus({ ...object, label: 'DOWN ODB', description: `${alert.issue_type || 'Active Down'} · ${alert.down_since || 'start time unavailable'} · ${alert.task_id || 'no ticket'}${object.township ? ` · ${object.township}` : ''}` });
    mapRef.flyTo([Number(object.lat), Number(object.lng)], zoom, { animate: true, duration: 0.7 });
    window.setTimeout(() => setHighlightedObjectId(id => id === object.object_id ? null : id), 3200);
  }, [mapRef]);

  // Focus on a network object, route, or DMA customer selected from unified global search.
  useEffect(() => {
    if (!mapRef) return;
    const selectedObject = focusObjectId ? objects.find(object => object.object_id === focusObjectId) : null;
    if (selectedObject) { window.setTimeout(() => focusNetworkObject(selectedObject), 250); return; }
    const selectedCustomer = focusUsername ? dmaCustomers.find(customer => String(customer.username) === focusUsername) : null;
    if (selectedCustomer) { window.setTimeout(() => focusDmaCustomer(selectedCustomer), 250); return; }
    if (focusCoords && focusCoords.length === 2) {
      const [lat, lng] = focusCoords;
      if (!isNaN(lat) && !isNaN(lng)) window.setTimeout(() => {
        setUnifiedSearchFocus({ lat, lng, name: focusObjectId || focusUsername || 'Search result', label: focusUsername ? 'DMA Customer' : 'Map result' });
        mapRef.flyTo([lat, lng], 18, { animate: true, duration: 0.7 });
      }, 250);
    }
  }, [dmaCustomers, focusCoords, focusObjectId, focusParam, focusUsername, focusDmaCustomer, focusNetworkObject, mapRef, objects]);

  // Open the existing read-only coverage or customer tools when Global Search hands off a typed location query.
  useEffect(() => {
    const query = globalSearchQuery.trim();
    if (!mapRef || !query) return;
    const handoffKey = `${globalSearchTool}:${query}`;
    if (globalSearchHandoffRef.current === handoffKey) return;
    globalSearchHandoffRef.current = handoffKey;
    if (!['coverage', 'customer'].includes(globalSearchTool)) {
      const q = normalizeMapSearchValue(query);
      const downNames = new Map((downAlerts || []).filter(alert => alert?.link_name).map(alert => [normalizeMapSearchValue(alert.link_name), alert]));
      const match = (objects || []).map(object => {
        if (object.obj_type !== 'odb') return object;
        const alert = [object.name, object.object_name, object.odb_name, object.link_name].map(normalizeMapSearchValue).map(name => downNames.get(name)).find(Boolean);
        return alert ? { ...object, status: 'down', down_alert: alert } : null;
      }).find(object => object && [object.name, object.object_name, object.odb_name, object.link_name, object.township, object.description, object.down_alert?.link_name, object.down_alert?.issue_type, object.down_alert?.task_id].some(value => normalizeMapSearchValue(value).includes(q)));
      if (match) window.setTimeout(() => focusDownAlert(match), 0); else toast.info('DOWN box not found on the current map');
      return;
    }
    setPanelOpen(true);
    setActiveTab('coverage');
    if (globalSearchTool === 'coverage') {
      setCustLookupMode(false);
      setCoverageAddress(query);
      setCoverageSuggestions([]);
      setShowSuggestions(false);
      window.setTimeout(() => handleCoverageGeocode(query), 0);
    } else {
      setCustLookupMode(true);
      setCustLookupQ(query);
      window.setTimeout(() => handleCustLookup(query), 0);
    }
  // The handoff is intentionally one-time for each query; query functions use their explicit argument.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, downAlerts, focusDownAlert, globalSearchQuery, globalSearchTool, mapRef]);
  const toggleLayer = (key) => setLayers(l => ({ ...l, [key]: !l[key] }));

  // ── Map click ──────────────────────────────────────────────────────────────
  const handleMapClick = useCallback(async (latlng) => {
    if (drawMode) {
      setDrawPoints(p => [...p, [latlng.lat, latlng.lng]]);
      return;
    }
    setClickPos(latlng);
    try {
      const res = await fetch(`${API}/fiber-map/distance`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: latlng.lat, lng: latlng.lng })
      });
      if (res.ok) setDistResult(await res.json());
    } catch {}
  }, [drawMode]);

  // ── Save object ────────────────────────────────────────────────────────────
  const saveObject = async (e) => {
    e.preventDefault();
    if (!clickPos) return;
    const payload = { obj_type: addType, lat: clickPos.lat, lng: clickPos.lng, ...addForm };
    if (addType === 'odb') { payload.capacity = Number(addForm.capacity); payload.used_ports = Number(addForm.used_ports); }
    if (addType === 'potential') payload.estimated_houses = Number(addForm.estimated_houses);
    try {
      const res = await fetch(`${API}/fiber-map/objects`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Object placed on map');
        setAddOpen(false);
        setAddForm({ name: '', capacity: 16, used_ports: 0, description: '', house_no: '', street: '', township: '', estimated_houses: 0, ratio: '1:8', alarm_status: 'Normal', parent_id: '', port_label: '' });
        load();
      }
    } catch { toast.error('Failed'); }
  };

  // ── Save route / cable ─────────────────────────────────────────────────────
  const saveRoute = async () => {
    if (drawPoints.length < 2) { toast.error('At least 2 points required'); return; }
    const lengthM = calcCableLength(drawPoints);
    let payload;
    if (drawMode === 'coverage_zone') {
      if (drawPoints.length < 3) { toast.error('At least 3 points for a zone'); return; }
      payload = {
        name: zoneForm.name || 'Coverage Zone',
        polygon: drawPoints,
        capacity: Number(zoneForm.capacity) || 100,
        potential_customers: Number(zoneForm.potential_customers) || 0,
        color: 'green',
      };
      await fetch(`${API}/fiber-map/coverage-zones`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      toast.success('Coverage zone saved');
    } else {
      payload = {
        obj_type: drawMode,
        name: drawCableForm.name || `${drawMode === 'backbone_route' ? 'Backbone' : drawMode === 'distribution_route' ? 'Distribution' : 'Drop'} Cable`,
        points: drawPoints,
        color: CABLE_COLORS[drawMode],
        cable_status: drawCableForm.cable_status || 'Active',
        length_meters: drawCableForm.length_meters ? Number(drawCableForm.length_meters) : lengthM,
        source_file: 'Manual',
      };
      await fetch(`${API}/fiber-map/objects`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      toast.success(`${drawMode === 'backbone_route' ? 'Backbone' : drawMode === 'distribution_route' ? 'Distribution' : 'Drop'} cable saved (${formatLength(lengthM)})`);
    }
    setDrawMode(null); setDrawPoints([]); setDrawCableForm({ name: '', cable_status: 'Active', length_meters: '' });
    load();
  };

  const deleteObj = async (id) => {
    if (!window.confirm('Delete this object?')) return;
    await fetch(`${API}/fiber-map/objects/${id}`, { method: 'DELETE' });
    toast.success('Deleted'); load();
  };

  // ── KML Import ─────────────────────────────────────────────────────────────
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(kml|kmz)$/i)) { toast.error('Only .kml and .kmz files supported'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/fiber-map/import-kml`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Import failed'); return; }
      toast.success(`Imported ${data.imported_count} objects from ${file.name}`);
      load();
    } catch (err) { toast.error(`Import error: ${err.message}`); }
    finally { setImporting(false); e.target.value = ''; }
  };

  // ── ODB Port Management ────────────────────────────────────────────────────
  const openPortManager = async (odb) => {
    setPortOpen(odb);
    try {
      const res = await fetch(`${API}/fiber-map/odb/${odb.object_id}/ports`);
      if (res.ok) setPortData(await res.json());
    } catch {}
    setPortForm({ port_number: '', customer_id: '', customer_name: '', customer_phone: '', customer_address: '', service_plan: '', onu_serial: '' });
  };

  const assignPort = async (e) => {
    e.preventDefault();
    if (!portOpen) return;
    try {
      const res = await fetch(`${API}/fiber-map/odb/${portOpen.object_id}/assign-port`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...portForm, port_number: Number(portForm.port_number) || undefined })
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed'); return; }
      toast.success(`Port ${data.port_number} assigned to ${data.customer_name}`);
      openPortManager(portOpen); load();
      setPortForm({ port_number: '', customer_id: '', customer_name: '', customer_phone: '', customer_address: '', service_plan: '', onu_serial: '' });
    } catch { toast.error('Failed'); }
  };

  const disconnectPort = async (assignmentId) => {
    if (!window.confirm('Disconnect this port?')) return;
    await fetch(`${API}/fiber-map/odb/port/${assignmentId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'Disconnected' }) });
    toast.success('Port disconnected'); openPortManager(portOpen); load();
  };

  // ── KMZ Layer Management ───────────────────────────────────────────────────
  const toggleKmzLayer = async (layerName) => {
    const newHidden = new Set(hiddenLayers);
    const nowVisible = newHidden.has(layerName);
    if (nowVisible) newHidden.delete(layerName); else newHidden.add(layerName);
    setHiddenLayers(newHidden);
    await fetch(`${API}/fiber-map/layers/${encodeURIComponent(layerName)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ visible: nowVisible }) });
  };

  const deleteKmzLayer = async (layerName) => {
    const layer = kmzLayers.find(l => l.layer_name === layerName);
    if (!window.confirm(`Delete layer "${layer?.display_name || layerName}" and all ${layer?.count || 0} objects?`)) return;
    const res = await fetch(`${API}/fiber-map/layers/${encodeURIComponent(layerName)}`, { method: 'DELETE' });
    if (res.ok) { const d = await res.json(); toast.success(`Deleted ${d.deleted_count} objects`); load(); }
  };

  const renameKmzLayer = async () => {
    if (!editLayer || !editLayerName.trim()) return;
    await fetch(`${API}/fiber-map/layers/${encodeURIComponent(editLayer)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ display_name: editLayerName.trim() }) });
    setEditLayer(null); load();
  };

  // ── Path Tracing ───────────────────────────────────────────────────────────
  const traceNetworkPath = async (customerId) => {
    if (!customerId) return;
    setTraceLoading(true);
    try {
      const res = await fetch(`${API}/fiber-map/path-trace/${encodeURIComponent(customerId)}`);
      if (res.ok) {
        const data = await res.json();
        setTracedPath(data);
        // Fly to first point in path
        if (data.path?.length > 0 && mapRef) {
          const first = data.path[0];
          if (first.lat && first.lng) mapRef.flyTo([first.lat, first.lng], 16);
        }
        toast.success(`Path traced: ${data.path_length} hops`);
      } else {
        toast.error('Path not found');
      }
    } catch { toast.error('Trace failed'); }
    finally { setTraceLoading(false); }
  };

  // ── Hierarchy popup ────────────────────────────────────────────────────────
  const openHierarchy = async (oid) => {
    try {
      const res = await fetch(`${API}/fiber-map/hierarchy/${oid}`);
      if (res.ok) { setHierarchyData(await res.json()); setHierarchyOpen(true); }
    } catch {}
  };

  // ── OLT/Splitter detail ────────────────────────────────────────────────────
  const openDetail = async (obj) => {
    try {
      let res;
      if (obj.obj_type === 'olt') res = await fetch(`${API}/fiber-map/olt/${obj.object_id}/details`);
      else if (obj.obj_type === 'splitter') res = await fetch(`${API}/fiber-map/splitter/${obj.object_id}/details`);
      else return;
      if (res.ok) { setDetailData({ type: obj.obj_type, ...(await res.json()) }); setDetailOpen(true); }
    } catch {}
  };

  const loadOdbPopupDetails = useCallback(async (odb) => {
    if (!odb?.object_id) return;
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [detailsResponse, connectionsResponse] = await Promise.all([
        fetch(`${API}/fiber-map/odb/${odb.object_id}/details`, { headers }),
        fetch(`${API}/fiber-map/odb/${odb.object_id}/connections`, { headers }),
      ]);
      if (detailsResponse.ok) {
        const details = await detailsResponse.json();
        setOdbPopupDetails(current => ({ ...current, [odb.object_id]: details }));
      }
      if (connectionsResponse.ok) {
        const connections = await connectionsResponse.json();
        setOdbConnections(current => ({ ...current, [odb.object_id]: connections }));
      }
      setSelectedOdbConnectionId(odb.object_id);
    } catch {}
  }, []);

  const copyOdbInfo = useCallback(async (odb, details) => {
    const capacity = details?.capacity ?? odb.capacity ?? 16;
    const used = details?.used ?? odb.used_ports ?? 0;
    const available = details?.available ?? Math.max(capacity - used, 0);
    const lines = [
      `ODB: ${odb.name || odb.object_id}`,
      `ID: ${odb.object_id}`,
      `Status: ${odbStatusLabel(odbStatusValue(odb))}`,
      `Ports: ${used}/${capacity}`,
      `Available: ${available}`,
      odb.township ? `Township: ${odb.township}` : null,
      (details?.street || odb.description) ? `Location: ${details?.street || odb.description}` : null,
      odb.source_file ? `Layer: ${odb.source_file}` : null,
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(lines);
      toast.success('ODB info copied');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = lines;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      copied ? toast.success('ODB info copied') : toast.error('Could not copy ODB info');
    }
  }, []);

  // ── Cable edit ─────────────────────────────────────────────────────────────
  const openCableEdit = (cable) => {
    setCableEditOpen(cable);
    setCableEditForm({ name: cable.name || '', cable_status: cable.cable_status || 'Active', length_meters: cable.length_meters || '' });
  };

  const saveCableEdit = async (e) => {
    e.preventDefault();
    if (!cableEditOpen) return;
    await fetch(`${API}/fiber-map/objects/${cableEditOpen.object_id}/cable-properties`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cableEditForm)
    });
    toast.success('Cable updated'); setCableEditOpen(null); load();
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  const normalizeBoxName = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const downAlertNames = useMemo(() => new Map(
    downAlerts
      .filter(alert => alert?.link_name)
      .map(alert => [normalizeBoxName(alert.link_name), alert])
  ), [downAlerts]);
  const effectiveObjects = useMemo(() => objects.map(object => {
    if (object.obj_type !== 'odb') return object;
    const candidates = [object.name, object.object_name, object.odb_name, object.link_name].map(normalizeBoxName).filter(Boolean);
    const alert = candidates.map(name => downAlertNames.get(name)).find(Boolean);
    return alert ? { ...object, status: 'down', down_alert: alert } : object;
  }), [objects, downAlertNames]);
  const odbStatusOptions = useMemo(() => [...new Set(effectiveObjects.filter(o => o.obj_type === 'odb').map(odbStatusValue))].sort(), [effectiveObjects]);
  const filteredOdbCount = useMemo(() => effectiveObjects.filter(o => o.obj_type === 'odb' && (odbStatusFilter === 'all' || odbStatusValue(o) === odbStatusFilter)).length, [effectiveObjects, odbStatusFilter]);
  const markers = useMemo(() => effectiveObjects.filter(o => !o.obj_type?.includes('route') && o.obj_type !== 'drop_cable' && layers[o.obj_type] && !hiddenLayers.has(o.source_file || 'Manual') && (o.obj_type !== 'odb' || odbStatusFilter === 'all' || odbStatusValue(o) === odbStatusFilter)), [hiddenLayers, layers, effectiveObjects, odbStatusFilter]);
  const routes = useMemo(() => effectiveObjects.filter(o => (o.obj_type?.includes('route') || o.obj_type === 'drop_cable') && layers[o.obj_type !== undefined ? o.obj_type : 'backbone_route'] && !hiddenLayers.has(o.source_file || 'Manual')), [hiddenLayers, layers, effectiveObjects]);
  // Traced path highlight
  const tracedObjectIds = new Set((tracedPath?.path || []).map(p => p.object_id));
  const tracedCableIds = new Set((tracedPath?.cables || []).map(c => c.object_id));

  const oltObjects = objects.filter(o => o.obj_type === 'olt');

  return (
    <div className="flex h-[calc(100dvh-4rem)] w-full min-h-0 m-0 relative overflow-hidden" data-testid="fiber-map-page">
      {/* ── Side Panel ───────────────────────────────────────────────────────── */}
      <div className={`${panelOpen ? 'w-[min(78vw,280px)] lg:w-[clamp(220px,14vw,260px)]' : 'w-0'} flex-shrink-0 bg-[#0F172A] text-white border-r border-[#1E293B] flex flex-col overflow-hidden transition-[width] duration-200`}>
        {/* Header */}
        <div className="p-3 border-b border-[#1E293B] flex items-center justify-between">
          <div>
            <h3 className="text-[13px] font-bold text-white">FTTX Network Map</h3>
            <p className="text-[9px] text-slate-400 mt-0.5">Myanmar Link ISP</p>
          </div>
          <button onClick={async () => { await clearAllCaches(); load(true); }} className="p-1.5 rounded-lg hover:bg-[#1E293B] text-slate-400 hover:text-white" title="Force refresh (clears cache)"><ArrowsClockwise size={14} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#1E293B]">
          {[
            { id: 'layers', label: 'Layers', icon: Stack },
            ...(canEdit ? [{ id: 'tools', label: 'Tools', icon: Plus }] : []),
            { id: 'trace', label: 'Trace', icon: Path },
            { id: 'coverage', label: 'Coverage', icon: MagnifyingGlass },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-medium transition-colors ${activeTab === t.id ? 'bg-[#1E40AF] text-white' : 'text-slate-400 hover:text-white'}`}>
              <t.icon size={12} />{t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* ── LAYERS TAB ─────────────────────────────────────────────────── */}
          {activeTab === 'layers' && (
            <div className="p-3 space-y-3">
              {/* Layer status indicators */}
              <div className="flex gap-1 mb-2 flex-wrap">
                {[
                  { key: 'objects', label: 'Network' },
                  { key: 'customers', label: 'Customers' },
                  { key: 'routes', label: 'Routes' },
                  { key: 'stats', label: 'Stats' },
                ].map(({ key, label }) => (
                  <span key={key} className="text-[8px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" style={{
                    background: layerStatus[key] === 'loaded' ? '#14532d' : layerStatus[key] === 'loading' ? '#1e3a5f' : layerStatus[key] === 'failed' ? '#7f1d1d' : '#1e293b',
                    color: layerStatus[key] === 'loaded' ? '#86efac' : layerStatus[key] === 'loading' ? '#93c5fd' : layerStatus[key] === 'failed' ? '#fca5a5' : '#94a3b8',
                  }}>
                    {layerStatus[key] === 'loaded' ? '✅' : layerStatus[key] === 'loading' ? '🔄' : layerStatus[key] === 'failed' ? '⚠️' : '⏸'} {label}
                  </span>
                ))}
              </div>
              {/* Fiber cables */}
              <div>
                <p className="text-[9px] font-bold uppercase text-slate-500 mb-1.5">Fiber Cables</p>
                {[
                  { key: 'backbone_route', label: 'Backbone Fiber', color: '#EF4444', desc: 'Thick red — main trunk' },
                  { key: 'distribution_route', label: 'Distribution Fiber', color: '#3B82F6', desc: 'Medium blue — feeder' },
                  { key: 'drop_cable', label: 'Drop Cable (Last Mile)', color: '#22C55E', desc: 'Thin green — to customer' },
                ].map(l => (
                  <label key={l.key} className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <input type="checkbox" checked={layers[l.key]} onChange={() => toggleLayer(l.key)} className="w-3 h-3 rounded" />
                    <div className="flex items-center gap-1.5 flex-1">
                      <div className="h-0.5 w-5 rounded flex-shrink-0" style={{ background: l.color, height: l.key === 'backbone_route' ? 4 : l.key === 'distribution_route' ? 2.5 : 1.5 }} />
                      <div>
                        <p className="text-[11px] text-white">{l.label}</p>
                        <p className="text-[9px] text-slate-500">{l.desc}</p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Network elements */}
              <div>
                <p className="text-[9px] font-bold uppercase text-slate-500 mb-1.5">Network Elements</p>
                {[
                  { key: 'olt', label: 'OLT Locations', color: '#BA1A1A' },
                  { key: 'odb', label: 'ODB Boxes', color: '#0052CC' },
                  { key: 'splitter', label: 'Splitters', color: '#7C4DFF' },
                  { key: 'customer', label: 'Customers', color: '#006D3B' },
                  { key: 'potential', label: 'Potential', color: '#F57F17' },
                  { key: 'coverage_zones', label: 'Coverage Zones', color: '#06B6D4' },
                ].map(l => (
                  <label key={l.key} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input type="checkbox" checked={layers[l.key]} onChange={() => toggleLayer(l.key)} className="w-3 h-3 rounded" />
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: l.color }} />
                    <span className="text-[11px] text-slate-300">{l.label}</span>
                  </label>
                ))}
                {layers.odb && (
                  <div className="mt-2 rounded-lg border border-[#334155] bg-[#111C30] p-2">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[9px] font-bold uppercase text-slate-400">ODB Status Filter</span>
                      <span className="text-[8px] text-slate-500">{filteredOdbCount}</span>
                    </div>
                    <select value={odbStatusFilter} onChange={event => setOdbStatusFilter(event.target.value)} className="w-full rounded border border-[#334155] bg-[#0F172A] px-2 py-1 text-[10px] text-white" aria-label="Filter ODB boxes by status">
                      <option value="all">All statuses</option>
                      {odbStatusOptions.map(status => <option key={status} value={status}>{odbStatusLabel(status)}</option>)}
                    </select>
                    <div className="mt-1.5 flex items-center gap-3 text-[8px] text-slate-400">
                      <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-green-600 ring-1 ring-green-300" />Online</span>
                      <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-slate-500 ring-1 ring-slate-300" />Unknown</span>
                    </div>
                  </div>
                )}
              </div>

        {/* DMA Customers */}
              <div className="border-t border-[#1E293B] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[9px] font-bold uppercase text-slate-500">DMA Customers ({dmaCustomers.length.toLocaleString()})</p>
                  <label className="flex items-center gap-1"><input type="checkbox" checked={showDmaCustomers} onChange={e => setShowDmaCustomers(e.target.checked)} className="w-3 h-3 rounded" /><span className="text-[9px] text-slate-400">Show</span></label>
                </div>
                {showDmaCustomers && (
                  <>
                    <div className="flex gap-1 mb-2">
                      {[{ v: 'all', l: 'All' }, { v: 'active', l: 'Active' }, { v: 'expired', l: 'No Service' }].map(f => (
                        <button key={f.v} onClick={() => setDmaFilter(f.v)} className={`text-[9px] px-2 py-0.5 rounded-full ${dmaFilter === f.v ? 'bg-blue-600 text-white' : 'bg-[#1E293B] text-slate-400'}`}>{f.l}</button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 text-[9px] text-slate-500 mt-1.5">
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500" />Active</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-600" />Inactive 30d+</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500" />No Service</span>
                    </div>
                  </>
                )}
              </div>

              {/* KMZ Layers */}
              <div className="border-t border-[#1E293B] pt-3">
                <p className="text-[9px] font-bold uppercase text-slate-500 mb-2">KMZ Layers ({kmzLayers.length})</p>
                {kmzLayers.length === 0 ? <p className="text-[10px] text-slate-500">No imported layers</p> :
                  kmzLayers.map(l => {
                    const isHidden = hiddenLayers.has(l.layer_name);
                    const isManual = l.layer_name === 'Manual';
                    return (
                      <div key={l.layer_name} className={`flex items-start gap-2 py-1.5 ${isHidden ? 'opacity-40' : ''}`}>
                        <input type="checkbox" checked={!isHidden} onChange={() => toggleKmzLayer(l.layer_name)} className="w-3 h-3 rounded mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          {editLayer === l.layer_name ? (
                            <div className="flex gap-1">
                              <input value={editLayerName} onChange={e => setEditLayerName(e.target.value)} className="bg-[#1E293B] border border-[#334155] rounded px-1.5 text-[10px] text-white h-6 flex-1" autoFocus onKeyDown={e => e.key === 'Enter' && renameKmzLayer()} />
                              <button onClick={renameKmzLayer} className="text-[9px] text-blue-400 font-bold">OK</button>
                              <button onClick={() => setEditLayer(null)} className="text-[9px] text-slate-500">X</button>
                            </div>
                          ) : (
                            <p className="text-[10px] text-slate-300 truncate cursor-pointer hover:text-white" onClick={() => { setEditLayer(l.layer_name); setEditLayerName(l.display_name); }}>{l.display_name}</p>
                          )}
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <span className="text-[9px] text-slate-500">{l.count} objects</span>
                            {l.types?.odb > 0 && <span className="bg-blue-800 text-blue-200 text-[8px] px-1 rounded">{l.types.odb} ODB</span>}
                            {l.types?.backbone_route > 0 && <span className="bg-red-900 text-red-200 text-[8px] px-1 rounded">{l.types.backbone_route} BB</span>}
                            {l.types?.distribution_route > 0 && <span className="bg-blue-900 text-blue-200 text-[8px] px-1 rounded">{l.types.distribution_route} Dist</span>}
                            {l.types?.olt > 0 && <span className="bg-red-900 text-red-200 text-[8px] px-1 rounded">{l.types.olt} OLT</span>}
                          </div>
                        </div>
                        {!isManual && <button onClick={() => deleteKmzLayer(l.layer_name)} className="p-0.5 rounded hover:bg-red-900 flex-shrink-0 mt-0.5" title="Delete layer"><svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/></svg></button>}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* ── TOOLS TAB (Admin + BMFT Supervisor only) ───────────────────── */}
          {activeTab === 'tools' && canEdit && (
            <div className="p-3 space-y-2">
              <p className="text-[9px] font-bold uppercase text-slate-500 mb-2">Place Objects</p>
              <button onClick={() => { setClickPos(null); setAddOpen(true); }} className="w-full bg-blue-700 hover:bg-blue-600 text-white text-[11px] py-2 rounded-lg flex items-center justify-center gap-1.5 font-medium">
                <Plus size={13} />Place Object
              </button>

              <p className="text-[9px] font-bold uppercase text-slate-500 mt-3 mb-2">Draw Fiber Cables</p>
              {[
                { mode: 'backbone_route', label: 'Draw Backbone', color: '#EF4444', desc: 'Thick red trunk cable' },
                { mode: 'distribution_route', label: 'Draw Distribution', color: '#3B82F6', desc: 'Medium blue feeder' },
                { mode: 'drop_cable', label: 'Draw Drop Cable', color: '#22C55E', desc: 'Thin green last mile' },
              ].map(d => (
                <button key={d.mode} onClick={() => { setDrawMode(drawMode === d.mode ? null : d.mode); setDrawPoints([]); }} className={`w-full text-[11px] py-2 rounded-lg flex items-center gap-2 px-3 font-medium border transition-colors ${drawMode === d.mode ? 'text-white border-transparent' : 'bg-[#1E293B] text-slate-300 border-[#334155] hover:border-slate-500'}`} style={drawMode === d.mode ? { background: d.color, borderColor: d.color } : {}}>
                  <div className="h-0.5 w-4 flex-shrink-0" style={{ background: d.color, height: d.mode === 'backbone_route' ? 4 : d.mode === 'distribution_route' ? 2.5 : 1.5 }} />
                  <div className="text-left"><p>{d.label}</p><p className="text-[8px] opacity-70">{d.desc}</p></div>
                </button>
              ))}

              {drawMode && drawMode !== 'coverage_zone' && (
                <div className="p-2.5 bg-[#1E293B] rounded-xl border border-[#334155] space-y-2">
                  <p className="text-[10px] font-bold text-yellow-400">Drawing: {drawMode === 'backbone_route' ? 'Backbone' : drawMode === 'distribution_route' ? 'Distribution' : 'Drop'} Cable</p>
                  <p className="text-[9px] text-slate-400">{drawPoints.length} points. Click map to add points.</p>
                  {drawPoints.length >= 2 && <p className="text-[9px] text-green-400 font-medium">Length: {formatLength(calcCableLength(drawPoints))}</p>}
                  <input value={drawCableForm.name} onChange={e => setDrawCableForm({ ...drawCableForm, name: e.target.value })} placeholder="Cable name (optional)" className="w-full bg-[#0F172A] border border-[#334155] rounded px-2 py-1 text-[10px] text-white placeholder-slate-600" />
                  <select value={drawCableForm.cable_status} onChange={e => setDrawCableForm({ ...drawCableForm, cable_status: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded px-2 py-1 text-[10px] text-white">
                    <option value="Active">Active</option>
                    <option value="Cut">Cut</option>
                    <option value="Degraded">Degraded</option>
                  </select>
                  <div className="flex gap-2">
                    <button onClick={saveRoute} className="flex-1 bg-green-700 hover:bg-green-600 text-white text-[10px] py-1.5 rounded-lg font-medium">Save</button>
                    <button onClick={() => { setDrawMode(null); setDrawPoints([]); }} className="flex-1 bg-[#334155] text-slate-300 text-[10px] py-1.5 rounded-lg">Cancel</button>
                  </div>
                </div>
              )}

              <p className="text-[9px] font-bold uppercase text-slate-500 mt-3 mb-2">Coverage Zone</p>
              <button onClick={() => { setDrawMode(drawMode === 'coverage_zone' ? null : 'coverage_zone'); setDrawPoints([]); }} className={`w-full text-[11px] py-2 rounded-lg flex items-center gap-2 px-3 font-medium border transition-colors ${drawMode === 'coverage_zone' ? 'bg-cyan-700 text-white border-cyan-600' : 'bg-[#1E293B] text-slate-300 border-[#334155] hover:border-slate-500'}`}>
                <Crosshair size={13} />Draw Coverage Zone
              </button>
              {drawMode === 'coverage_zone' && (
                <div className="p-2.5 bg-[#1E293B] rounded-xl border border-[#334155] space-y-2">
                  <p className="text-[10px] font-bold text-cyan-400">Drawing Coverage Zone</p>
                  <p className="text-[9px] text-slate-400">{drawPoints.length} points. Min 3 to close.</p>
                  <input value={zoneForm.name} onChange={e => setZoneForm({ ...zoneForm, name: e.target.value })} placeholder="Zone name" className="w-full bg-[#0F172A] border border-[#334155] rounded px-2 py-1 text-[10px] text-white placeholder-slate-600" />
                  <input type="number" value={zoneForm.capacity} onChange={e => setZoneForm({ ...zoneForm, capacity: e.target.value })} placeholder="Capacity (households)" className="w-full bg-[#0F172A] border border-[#334155] rounded px-2 py-1 text-[10px] text-white placeholder-slate-600" />
                  <input type="number" value={zoneForm.potential_customers} onChange={e => setZoneForm({ ...zoneForm, potential_customers: e.target.value })} placeholder="Potential customers" className="w-full bg-[#0F172A] border border-[#334155] rounded px-2 py-1 text-[10px] text-white placeholder-slate-600" />
                  <div className="flex gap-2">
                    <button onClick={saveRoute} disabled={drawPoints.length < 3} className="flex-1 bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 text-white text-[10px] py-1.5 rounded-lg font-medium">Save Zone</button>
                    <button onClick={() => { setDrawMode(null); setDrawPoints([]); }} className="flex-1 bg-[#334155] text-slate-300 text-[10px] py-1.5 rounded-lg">Cancel</button>
                  </div>
                </div>
              )}

              <p className="text-[9px] font-bold uppercase text-slate-500 mt-3 mb-2">Import</p>
              <label className={`w-full bg-[#1E293B] hover:bg-[#334155] text-slate-300 text-[11px] py-2 rounded-lg flex items-center justify-center gap-1.5 cursor-pointer border border-[#334155] ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
                <DownloadSimple size={13} />{importing ? 'Importing...' : 'Import KMZ/KML'}
                <input type="file" accept=".kml,.kmz" onChange={handleFileImport} className="hidden" />
              </label>
            </div>
          )}

          {/* ── TRACE TAB ──────────────────────────────────────────────────── */}
          {activeTab === 'trace' && (
            <div className="p-3 space-y-3">
              <p className="text-[9px] font-bold uppercase text-slate-500 mb-2">Network Path Tracing</p>
              <p className="text-[10px] text-slate-400">Enter a customer PPPoE ID or object ID to trace the full fiber path back to the OLT.</p>
              <div className="flex gap-2">
                <input value={tracingId} onChange={e => setTracingId(e.target.value)} placeholder="PPPoE ID or Customer ID" className="flex-1 bg-[#1E293B] border border-[#334155] rounded-lg px-2 py-1.5 text-[10px] text-white placeholder-slate-500" onKeyDown={e => e.key === 'Enter' && traceNetworkPath(tracingId)} />
                <button onClick={() => traceNetworkPath(tracingId)} disabled={traceLoading || !tracingId} className="bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-[10px] px-3 py-1.5 rounded-lg font-medium">
                  {traceLoading ? '...' : 'Trace'}
                </button>
              </div>
              {tracedPath && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-white">Path: {tracedPath.path_length} hops</p>
                    <button onClick={() => setTracedPath(null)} className="text-[9px] text-slate-500 hover:text-white">Clear</button>
                  </div>
                  <div className="space-y-1">
                    {(tracedPath.path || []).map((p, i) => (
                      <div key={p.object_id || i} className="flex items-center gap-2 p-1.5 bg-[#1E293B] rounded-lg">
                        <div className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold flex-shrink-0" style={{ background: p.obj_type === 'olt' ? '#BA1A1A' : p.obj_type === 'splitter' ? '#7C4DFF' : p.obj_type === 'odb' ? '#0052CC' : '#006D3B' }}>{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-white truncate">{p.name || p.object_id}</p>
                          <p className="text-[8px] text-slate-500 uppercase">{p.obj_type}</p>
                        </div>
                        {p.port_label && <span className="text-[8px] text-slate-400">{p.port_label}</span>}
                      </div>
                    ))}
                  </div>
                  {!tracedPath.has_full_path && <p className="text-[9px] text-yellow-400">⚠ Incomplete path — some links missing</p>}
                </div>
              )}

              {/* Quick OLT list */}
              <div className="border-t border-[#1E293B] pt-3">
                <p className="text-[9px] font-bold uppercase text-slate-500 mb-2">OLT Locations ({oltObjects.length})</p>
                {oltObjects.map(o => (
                  <div key={o.object_id} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-[#1E293B] rounded px-1" onClick={() => { if (mapRef && o.lat && o.lng) mapRef.flyTo([o.lat, o.lng], 16); }}>
                    <div className="w-4 h-4 rounded bg-red-800 flex items-center justify-center text-[7px] text-white font-bold flex-shrink-0">OLT</div>
                    <span className="text-[10px] text-slate-300 truncate">{o.name}</span>
                  </div>
                ))}
              </div>

              {/* Distance analysis */}
              {distResult && clickPos && (
                <div className="border-t border-[#1E293B] pt-3">
                  <p className="text-[9px] font-bold uppercase text-slate-500 mb-2">Distance Analysis</p>
                  <div className="p-2.5 rounded-xl" style={{ background: `${ZONE_COLORS[distResult.zone]}20`, border: `1px solid ${ZONE_COLORS[distResult.zone]}40` }}>
                    <p className="text-[11px] font-bold" style={{ color: ZONE_COLORS[distResult.zone] }}>{distResult.label}</p>
                    <p className="text-[10px] text-slate-300 mt-1">Nearest ODB: <span className="font-bold text-white">{distResult.odb_name || 'None'}</span></p>
                    <p className="text-[10px] text-slate-300">Distance: <span className="font-bold text-white tabular-nums">{distResult.distance_meters} m</span></p>
                    <p className="text-[10px] text-slate-300">Cable needed: <span className="font-bold text-white tabular-nums">{distResult.fiber_cable_meters} m</span></p>
                    <div className="flex gap-2 mt-2 text-[9px] text-slate-400">
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-600" />&lt;200m</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-yellow-500" />200-500m</span>
                      <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-600" />&gt;500m</span>
                    </div>
                  </div>
                  {canEdit && <button onClick={() => { setAddType('potential'); setAddOpen(true); }} className="w-full mt-2 bg-[#1E293B] hover:bg-[#334155] text-slate-300 text-[10px] py-1.5 rounded-lg border border-[#334155]">Mark as Potential Customer</button>}
                </div>
              )}
            </div>
          )}
          {/* ── COVERAGE TAB ──────────────────────────────────────────────── */}
          {activeTab === 'coverage' && (
            <div className="p-3 space-y-3">
              {/* Toggle: Coverage Check vs Existing Customer */}
              <div className="flex rounded-lg overflow-hidden border border-[#334155]">
                <button onClick={() => setCustLookupMode(false)} className={`flex-1 py-1.5 text-[10px] font-medium transition-colors ${!custLookupMode ? 'bg-[#1E40AF] text-white' : 'text-slate-400 hover:text-white'}`}>
                  📍 Coverage Check
                </button>
                <button onClick={() => setCustLookupMode(true)} className={`flex-1 py-1.5 text-[10px] font-medium transition-colors ${custLookupMode ? 'bg-[#1E40AF] text-white' : 'text-slate-400 hover:text-white'}`}>
                  👤 Customer Lookup
                </button>
              </div>

              {!custLookupMode ? (
                <div className="space-y-3">
                  <p className="text-[9px] font-bold uppercase text-slate-500">Coverage Check</p>
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        value={coverageAddress}
                        onChange={e => handleCoverageAddressChange(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { setShowSuggestions(false); handleCoverageGeocode(); } if (e.key === 'Escape') setShowSuggestions(false); }}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        onFocus={() => coverageSuggestions.length > 0 && setShowSuggestions(true)}
                        placeholder="Enter address, township, or area..."
                        className="w-full bg-[#1E293B] border border-[#334155] rounded-lg px-2 py-1.5 text-[10px] text-white placeholder-slate-500"
                      />
                      {showSuggestions && coverageSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-[9999] bg-[#0F172A] border border-[#334155] rounded-lg mt-0.5 shadow-2xl overflow-hidden">
                          {coverageSuggestions.map((s, i) => (
                            <button
                              key={i}
                              onMouseDown={() => handleSuggestionSelect(s)}
                              className="w-full text-left px-2 py-1.5 text-[9px] text-slate-300 hover:bg-[#1E293B] border-b border-[#1E293B] last:border-0 truncate"
                            >
                              📍 {s.display_name.substring(0, 70)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button
                        onClick={handleCoverageGPS}
                        className="flex-1 bg-[#1E293B] hover:bg-[#334155] border border-[#334155] text-slate-300 text-[9px] py-1.5 rounded-lg"
                      >
                        📍 Use My Location
                      </button>
                      <button
                        onClick={handleCoverageGeocode}
                        disabled={coverageGeoLoading || !coverageAddress}
                        className="flex-1 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-[9px] py-1.5 rounded-lg font-medium"
                      >
                        {coverageGeoLoading ? '...' : '🔍 Search'}
                      </button>
                    </div>
                  </div>
                  {coverageCoords && (
                    <div className="bg-[#1E293B] rounded-lg p-2 text-[9px] text-slate-400">
                      📌 Lat: <span className="text-white font-mono">{coverageCoords.lat.toFixed(5)}</span>
                      &nbsp;Lng: <span className="text-white font-mono">{coverageCoords.lng.toFixed(5)}</span>
                    </div>
                  )}
                  {coverageCoords && (
                    <button
                      onClick={handleCheckCoverage}
                      disabled={coverageLoading}
                      className="w-full bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-[10px] py-2 rounded-lg font-bold"
                    >
                      {coverageLoading ? 'Checking...' : '✅ Check Coverage'}
                    </button>
                  )}
                  {coverageResult && (
                    <div className={`rounded-xl p-3 border ${coverageResult.covered ? 'bg-emerald-900/30 border-emerald-700' : 'bg-red-900/30 border-red-700'}`}>
                      <p className={`text-[13px] font-bold mb-2 ${coverageResult.covered ? 'text-emerald-400' : 'text-red-400'}`}>
                        {coverageResult.covered ? '✅ Covered' : '❌ Not Covered'}
                      </p>
                      {coverageResult.nearest_odb && (
                        <div className="space-y-1 text-[10px]">
                          <p className="text-slate-300">Nearest ODB: <span className="text-white font-bold">{coverageResult.nearest_odb.name}</span></p>
                          <p className="text-slate-300">Distance: <span className="text-white font-bold tabular-nums">{coverageResult.distance_m} m</span></p>
                          <p className="text-slate-300">Available Ports: <span className="text-white font-bold">{coverageResult.available_ports}</span></p>
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold mt-1 ${
                            coverageResult.feasibility === 'Easy' ? 'bg-emerald-800 text-emerald-300' :
                            coverageResult.feasibility === 'Possible' ? 'bg-yellow-800 text-yellow-300' :
                            'bg-red-800 text-red-300'
                          }`}>
                            {coverageResult.feasibility === 'Easy' ? '🟢' : coverageResult.feasibility === 'Possible' ? '🟡' : '🔴'} {coverageResult.feasibility} Installation
                          </div>
                        </div>
                      )}
                      {coverageResult.covered && canEdit && coverageResult.nearest_odb && (
                        <button
                          onClick={() => {
                            const odb = coverageResult.nearest_odb;
                            window.open(`/customers/new?odb=${encodeURIComponent(odb.name)}&odb_id=${odb.object_id}&lat=${coverageCoords.lat}&lng=${coverageCoords.lng}`, '_blank');
                          }}
                          className="w-full mt-2 bg-blue-700 hover:bg-blue-600 text-white text-[10px] py-1.5 rounded-lg font-bold"
                        >
                          🆕 Register New Customer
                        </button>
                      )}
                      {/* Get Directions button */}
                      {coverageResult.nearest_odb?.lat && (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex gap-1">
                            <button
                              onClick={() => { setRouteMode('driving'); handleGetDirections('driving'); }}
                              disabled={routeLoading}
                              className={`flex-1 text-[9px] py-1.5 rounded-lg font-bold flex items-center justify-center gap-1 ${routeMode === 'driving' && routeData ? 'bg-blue-600 text-white' : 'bg-[#1E293B] hover:bg-blue-700 text-blue-300 hover:text-white border border-blue-800'}`}
                            >
                              🚗 Drive
                            </button>
                            <button
                              onClick={() => { setRouteMode('walking'); handleGetDirections('walking'); }}
                              disabled={routeLoading}
                              className={`flex-1 text-[9px] py-1.5 rounded-lg font-bold flex items-center justify-center gap-1 ${routeMode === 'walking' && routeData ? 'bg-blue-600 text-white' : 'bg-[#1E293B] hover:bg-blue-700 text-blue-300 hover:text-white border border-blue-800'}`}
                            >
                              🚶 Walk
                            </button>
                          </div>
                          {routeLoading && <p className="text-[9px] text-blue-400 text-center animate-pulse">🧭 Calculating route...</p>}
                          {routeData && (
                            <div className="bg-blue-950/50 border border-blue-800 rounded-lg p-2">
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-[10px] font-bold text-blue-300">
                                  🧭 Navigate to {routeData.odbName}
                                </p>
                                <button onClick={clearRoute} className="text-[8px] text-slate-500 hover:text-red-400">✕ Clear</button>
                              </div>
                              <div className="flex gap-3 mb-1.5">
                                <span className="text-[10px] text-white font-bold">📏 {routeData.distance < 1000 ? `${Math.round(routeData.distance)}m` : `${(routeData.distance/1000).toFixed(1)}km`}</span>
                                <span className="text-[10px] text-white font-bold">⏱ ~{Math.round(routeData.duration/60)} min</span>
                              </div>
                              <button
                                onClick={() => setShowDirections(d => !d)}
                                className="text-[8px] text-blue-400 hover:text-blue-200 underline"
                              >
                                {showDirections ? '▲ Hide' : '▼ Show'} turn-by-turn ({routeData.steps.length} steps)
                              </button>
                              {showDirections && (
                                <div className="mt-1.5 max-h-40 overflow-y-auto space-y-0.5">
                                  {routeData.steps.map((step, i) => {
                                    const { icon, text, dist } = formatStepInstruction(step);
                                    return (
                                      <div key={i} className="flex items-start gap-1.5 py-0.5 border-b border-blue-900/50">
                                        <span className="text-[11px] flex-shrink-0 w-4">{icon}</span>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[9px] text-slate-200 truncate">{text || 'Continue'}</p>
                                        </div>
                                        <span className="text-[8px] text-slate-500 flex-shrink-0">{dist}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => { setCoverageResult(null); setCoverageCoords(null); setCoverageMarker(null); setCoverageRadius(null); setCoverageAddress(''); setCoverageSuggestions([]); setShowSuggestions(false); clearRoute(); }}
                        className="w-full mt-1 text-slate-500 hover:text-slate-300 text-[9px] py-1"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[9px] font-bold uppercase text-slate-500">Existing Customer Lookup</p>
                  <div className="flex gap-1.5">
                    <input
                      value={custLookupQ}
                      onChange={e => setCustLookupQ(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCustLookup()}
                      placeholder="Name, SN, or PPPoE ID..."
                      className="flex-1 bg-[#1E293B] border border-[#334155] rounded-lg px-2 py-1.5 text-[10px] text-white placeholder-slate-500"
                    />
                    <button
                      onClick={handleCustLookup}
                      disabled={custLookupLoading || !custLookupQ}
                      className="bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white text-[10px] px-3 py-1.5 rounded-lg font-medium"
                    >
                      {custLookupLoading ? '...' : '🔍'}
                    </button>
                  </div>
                  {custLookupResults.length > 0 && (
                    <div className="space-y-2">
                      {custLookupResults.map((c, i) => (
                        <div
                          key={i}
                          className="bg-[#1E293B] rounded-xl p-2.5 cursor-pointer hover:bg-[#334155] transition-colors"
                          onClick={() => {
                            if (c.lat && c.lng && mapRef) {
                              mapRef.flyTo([c.lat, c.lng], 17);
                              setCoverageMarker({ lat: c.lat, lng: c.lng });
                            }
                          }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] font-bold text-white truncate">{c.name || c.username}</p>
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${
                              c.status === 'Active' ? 'bg-emerald-800 text-emerald-300' :
                              c.status === 'Offline' ? 'bg-red-800 text-red-300' :
                              'bg-yellow-800 text-yellow-300'
                            }`}>{c.status}</span>
                          </div>
                          <p className="text-[9px] text-slate-400">PPPoE: <span className="text-slate-300">{c.username}</span></p>
                          {c.odb_name && <p className="text-[9px] text-slate-400">ODB: <span className="text-slate-300">{c.odb_name}</span></p>}
                          {c.lat && c.lng ? (
                            <p className="text-[9px] text-emerald-400 mt-1">📍 {c.lat.toFixed(4)}, {c.lng.toFixed(4)} — click to view</p>
                          ) : (
                            <p className="text-[9px] text-yellow-500 mt-1">⚠ No GPS location set</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {custLookupResults.length === 0 && custLookupQ && !custLookupLoading && (
                    <p className="text-[10px] text-slate-500 text-center py-4">No customers found</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toggle panel button */}
      <button onClick={() => setPanelOpen(!panelOpen)} className="absolute z-[1000] bg-[#0F172A] border border-[#334155] rounded-r-lg px-1 py-3 shadow-lg text-slate-400 hover:text-white top-1/2 -translate-y-1/2" style={{ left: panelOpen ? 300 : 0 }}>
        {panelOpen ? '◀' : '▶'}
      </button>

      {/* ── Map ──────────────────────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 min-h-0 relative">
        <MapContainer center={YANGON} zoom={14} style={{ height: '100%', width: '100%' }} zoomControl={true} preferCanvas ref={setMapRef}>
          <LayersControl position="topright">
            {/* Mapbox Streets: fast vector-style basemap; public token is injected at build time. */}
            {MAPBOX_TOKEN && <LayersControl.BaseLayer checked name="🗺 Mapbox Streets">
              <TileLayer
                url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/256/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                attribution='&copy; Mapbox &copy; OpenStreetMap'
                maxZoom={22}
                updateWhenIdle={true}
                keepBuffer={2}
              />
            </LayersControl.BaseLayer>}
            {/* Google Satellite (fallback) */}
            <LayersControl.BaseLayer checked={!MAPBOX_TOKEN} name="🛰 Google Satellite">
              <TileLayer
                url="/gsat/?x={x}&y={y}&z={z}"
                attribution='&copy; Google'
                maxZoom={22}
              />
            </LayersControl.BaseLayer>
            {/* Google Hybrid (Satellite + Labels) */}
            <LayersControl.BaseLayer name="🛰 Google Hybrid">
              <TileLayer
                url="/ghybrid/?x={x}&y={y}&z={z}"
                attribution='&copy; Google'
                maxZoom={22}
              />
            </LayersControl.BaseLayer>
            {/* Google Roads */}
            <LayersControl.BaseLayer name="🗺 Google Roads">
              <TileLayer
                url="/tiles/{z}/{x}/{y}.png"
                attribution='&copy; Google'
                maxZoom={22}
              />
            </LayersControl.BaseLayer>
            {/* Esri Satellite (fallback) */}
            <LayersControl.BaseLayer name="🌍 Esri Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; Esri, Maxar, Earthstar'
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            {/* OSM Street */}
            <LayersControl.BaseLayer name="🗺 Street Map (OSM)">
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            </LayersControl.BaseLayer>
          </LayersControl>

          <MapClickHandler onMapClick={handleMapClick} />
          <UnifiedSearchPopup focus={unifiedSearchFocus} />

          {/* ── Coverage Zones ────────────────────────────────────────────── */}
          {layers.coverage_zones && coverageZones.map(z => {
            const used = z.used || 0;
            const cap = z.capacity || 100;
            const pct = (used / cap) * 100;
            const zColor = pct >= 90 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#22C55E';
            return (
              <Polygon key={z.zone_id} positions={z.polygon || []} color={zColor} fillColor={zColor} fillOpacity={0.12} weight={2} dashArray="6 3">
                <Tooltip permanent direction="center" className="bg-transparent border-0 shadow-none">
                  <div className="bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap">
                    {z.name} | {z.potential_customers || 0} potential
                  </div>
                </Tooltip>
                <Popup>
                  <div className="text-xs min-w-[180px]">
                    <p className="font-bold text-sm">{z.name}</p>
                    <p className="text-[10px] text-gray-500">Coverage Zone</p>
                    <div className="mt-1.5 space-y-1">
                      <PortBar used={used} total={cap} label="Capacity" />
                      <p>Potential: <span className="font-bold">{z.potential_customers || 0}</span></p>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${pct >= 90 ? 'bg-red-100 text-red-700' : pct >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>{pct >= 90 ? '🔴 Full' : pct >= 70 ? '🟡 Near Full' : '🟢 Available'}</span>
                    </div>
                    {canEdit && <button onClick={async () => { if (window.confirm('Delete this zone?')) { await fetch(`${API}/fiber-map/coverage-zones/${z.zone_id}`, { method: 'DELETE' }); load(); toast.success('Zone deleted'); } }} className="text-red-600 text-[10px] mt-2 underline">Delete Zone</button>}
                  </div>
                </Popup>
              </Polygon>
            );
          })}

          {/* ── Fiber Routes ──────────────────────────────────────────────── */}
          {routes.map(r => {
            if (!r.points || r.points.length < 2) return null;
            const baseColor = CABLE_COLORS[r.obj_type] || r.color || '#3B82F6';
            const statusColor = CABLE_STATUS_COLORS[r.cable_status];
            const lineColor = statusColor || baseColor;
            const isTraced = tracedCableIds.has(r.object_id);
            const isSearchHighlighted = highlightedObjectId === r.object_id;
            const weight = r.obj_type === 'backbone_route' ? 5 : r.obj_type === 'distribution_route' ? 3 : 2;
            const lengthM = r.length_meters || calcCableLength(r.points);
            return (
              <Polyline
                key={r.object_id}
                positions={r.points}
                color={isSearchHighlighted ? '#FBBF24' : isTraced ? '#F59E0B' : lineColor}
                weight={isSearchHighlighted ? weight + 4 : isTraced ? weight + 3 : weight}
                opacity={r.cable_status === 'Cut' ? 0.9 : 0.85}
                dashArray={r.obj_type === 'distribution_route' ? '10 5' : r.cable_status === 'Cut' ? '4 4' : undefined}
              >
                <Tooltip sticky>
                  <div className="text-xs">
                    <p className="font-bold">{r.name || r.obj_type}</p>
                    <p>{r.obj_type === 'backbone_route' ? '🔴 Backbone' : r.obj_type === 'distribution_route' ? '🔵 Distribution' : '🟢 Drop Cable'}</p>
                    <p>Length: <span className="font-bold tabular-nums">{formatLength(lengthM)}</span></p>
                    <p>Status: <span className={`font-bold ${r.cable_status === 'Cut' ? 'text-red-600' : r.cable_status === 'Degraded' ? 'text-orange-500' : 'text-green-600'}`}>{r.cable_status || 'Active'}</span></p>
                  </div>
                </Tooltip>
                <Popup>
                  <div className="text-xs min-w-[180px]">
                    <p className="font-bold text-sm">{r.name}</p>
                    <p className="text-[10px] text-gray-500">{r.obj_type === 'backbone_route' ? '🔴 Backbone Fiber' : r.obj_type === 'distribution_route' ? '🔵 Distribution Fiber' : '🟢 Drop Cable'}</p>
                    <div className="mt-1.5 space-y-0.5">
                      <p>Length: <span className="font-bold tabular-nums">{formatLength(lengthM)}</span></p>
                      <p>Status: <span className={`font-bold ${r.cable_status === 'Cut' ? 'text-red-600' : r.cable_status === 'Degraded' ? 'text-orange-500' : 'text-green-600'}`}>{r.cable_status || 'Active'}</span></p>
                      <p>Points: {r.points.length}</p>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => openCableEdit(r)} className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded">Edit</button>
                        <button onClick={() => deleteObj(r.object_id)} className="text-[10px] bg-red-600 text-white px-2 py-0.5 rounded">Delete</button>
                      </div>
                    )}
                  </div>
                </Popup>
              </Polyline>
            );
          })}

          {/* ── Selected ODB → customer service paths (assignment-backed only) ── */}
          {(() => {
            const connectionSet = selectedOdbConnectionId ? odbConnections[selectedOdbConnectionId] : null;
            const selectedOdb = selectedOdbConnectionId ? objects.find(o => o.object_id === selectedOdbConnectionId) : null;
            if (!connectionSet || !selectedOdb || !Number.isFinite(Number(selectedOdb.lat)) || !Number.isFinite(Number(selectedOdb.lng))) return null;
            return (connectionSet.connections || []).filter(connection => connection.has_location && Number.isFinite(Number(connection.lat)) && Number.isFinite(Number(connection.lng))).map(connection => {
              const isActive = connection.state === 'active';
              return <Polyline key={`odb-connection-${connection.assignment_id || connection.customer_id}`} positions={[[Number(selectedOdb.lat), Number(selectedOdb.lng)], [Number(connection.lat), Number(connection.lng)]]} color={isActive ? '#16A34A' : '#94A3B8'} weight={isActive ? 4 : 1.5} opacity={isActive ? 0.92 : 0.68} dashArray={isActive ? undefined : '4 7'}>
                <Tooltip sticky><div className="text-xs"><p className="font-bold">{connection.customer_name || connection.customer_id}</p><p>ODB Port: {connection.port_number || '—'}</p><p className={isActive ? 'text-green-700 font-bold' : 'text-slate-600 font-bold'}>{isActive ? '● Active service' : '● Inactive / no service'}</p></div></Tooltip>
              </Polyline>;
            });
          })()}

          {/* Drawing preview */}
          {drawPoints.length > 1 && drawMode !== 'coverage_zone' && (
            <Polyline positions={drawPoints} color={CABLE_COLORS[drawMode] || '#3B82F6'} weight={3} dashArray="6 4" opacity={0.8} />
          )}
          {drawPoints.length > 2 && drawMode === 'coverage_zone' && (
            <Polygon positions={drawPoints} color="#06B6D4" fillColor="#06B6D4" fillOpacity={0.15} weight={2} dashArray="6 3" />
          )}

          {/* ── Network Markers — clustered separately from DMA customers ─── */}
          <MarkerClusterGroup chunkedLoading removeOutsideVisibleBounds maxClusterRadius={52} disableClusteringAtZoom={14} spiderfyOnMaxZoom={true} showCoverageOnHover={false} zoomToBoundsOnClick animate>
          {markers.map(o => {
            const isTraced = tracedObjectIds.has(o.object_id);
            const odbStatus = o.obj_type === 'odb' ? odbStatusValue(o) : null;
            const odbDetails = o.obj_type === 'odb' ? odbPopupDetails[o.object_id] : null;
            const odbConnectionSet = o.obj_type === 'odb' ? odbConnections[o.object_id] : null;
            const icon = highlightedObjectId === o.object_id ? makeSearchHighlightIcon(o.obj_type) : (isTraced ? makeIcon('#F59E0B', o.obj_type === 'olt' ? 32 : o.obj_type === 'odb' ? 26 : 22, o.obj_type === 'olt' ? 'OLT' : o.obj_type === 'odb' ? 'ODB' : o.obj_type === 'splitter' ? 'SP' : '') : (o.obj_type === 'odb' ? makeOdbIcon(odbStatus, 28) : (ICONS[o.obj_type] || ICONS.customer)));
            return (
              <Marker key={o.object_id} position={[o.lat, o.lng]} icon={icon} eventHandlers={o.obj_type === 'odb' ? { click: () => loadOdbPopupDetails(o) } : undefined}>
                <Popup>
                  <div className="text-xs min-w-[200px]">
                    <p className="font-bold text-sm">{o.name || o.object_id}</p>
                    <p className="text-[10px] text-gray-500 uppercase">{o.obj_type}</p>

                    {/* OLT popup */}
                    {o.obj_type === 'olt' && (
                      <div className="mt-1.5 space-y-1">
                        {o.description && <p className="text-[10px]">{o.description}</p>}
                        <AlarmBadge status={o.alarm_status} />
                        {o.port_label && <p className="text-[10px]">Ports: {o.port_label}</p>}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => openDetail(o)} className="text-[10px] bg-red-700 text-white px-2 py-0.5 rounded font-medium">Details</button>
                          <button onClick={() => openHierarchy(o.object_id)} className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded">Hierarchy</button>
                        </div>
                        {canEdit && <button onClick={() => deleteObj(o.object_id)} className="text-red-600 text-[10px] mt-1 underline">Delete</button>}
                      </div>
                    )}

                    {/* ODB popup */}
                    {o.obj_type === 'odb' && (
                      <div className="mt-1.5 space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] text-gray-600">ID: <span className="font-mono text-gray-800">{o.object_id}</span></span>
                          <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${odbStatus === 'online' ? 'bg-green-100 text-green-700' : ['offline', 'down', 'inactive'].includes(odbStatus) ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{odbStatusLabel(odbStatus)}</span>
                        </div>
                        {o.down_alert && <div className="rounded bg-red-50 border border-red-200 px-1.5 py-1 text-[10px] text-red-800 space-y-0.5"><p className="font-bold">DOWN DETAILS</p><p>Reason: {o.down_alert.issue_type || 'Active Down'}</p><p>Down since: {o.down_alert.down_since || 'Unavailable'}</p>{o.down_alert.task_id && <p>Ticket: {o.down_alert.task_id}</p>}{o.down_alert.priority != null && <p>Priority: {o.down_alert.priority}</p>}{o.down_alert.impacted_users != null && <p>Impacted users: {o.down_alert.impacted_users}</p>}</div>}
                        <PortBar used={odbDetails?.used ?? o.used_ports ?? 0} total={odbDetails?.capacity ?? o.capacity ?? 16} label="Ports" />
                        <p className="text-[10px]">Available: <span className="font-bold text-blue-600">{odbDetails?.available ?? Math.max((o.capacity || 16) - (o.used_ports || 0), 0)}</span></p>
                        {o.township && <p className="text-[10px]">Township: {o.township}</p>}
                        {(odbDetails?.street || o.description) && <p className="text-[10px]">Location: {odbDetails?.street || o.description}</p>}
                        {o.source_file && <p className="text-[9px] text-gray-500">Layer: {o.source_file}</p>}
                        {odbDetails && <p className="text-[10px]">Services: <span className="font-bold text-green-700">{odbDetails.active_count || 0} active</span> · <span className="font-bold text-red-600">{odbDetails.offline_count || 0} offline</span></p>}
                        {odbConnectionSet && <p className="text-[10px]">Map paths: <span className="font-bold text-green-700">{odbConnectionSet.active_count || 0} active</span> · <span className="font-bold text-slate-600">{odbConnectionSet.inactive_count || 0} inactive</span>{odbConnectionSet.no_location_count ? ` · ${odbConnectionSet.no_location_count} no location` : ''}</p>}
                        {selectedOdbConnectionId === o.object_id && odbConnectionSet && <p className="rounded bg-slate-50 px-1.5 py-1 text-[9px] text-slate-600">Green = active connection · thin gray = inactive/no-service</p>}
                        {o.alarm_status && <AlarmBadge status={o.alarm_status} />}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => copyOdbInfo(o, odbDetails)} className="text-[10px] bg-emerald-700 text-white px-2 py-0.5 rounded font-medium">Copy Info</button>
                          <button onClick={() => openPortManager(o)} className="text-[10px] bg-blue-700 text-white px-2 py-0.5 rounded font-medium">Manage Ports</button>
                          <button onClick={() => openHierarchy(o.object_id)} className="text-[10px] bg-slate-600 text-white px-2 py-0.5 rounded">Hierarchy</button>
                        </div>
                        {canEdit && <button onClick={() => deleteObj(o.object_id)} className="text-red-600 text-[10px] mt-1 underline">Delete</button>}
                      </div>
                    )}

                    {/* Splitter popup */}
                    {o.obj_type === 'splitter' && (
                      <div className="mt-1.5 space-y-1">
                        <p className="text-[10px]">Ratio: <span className="font-bold">{o.ratio || '1:8'}</span></p>
                        <AlarmBadge status={o.alarm_status} />
                        {o.description && <p className="text-[10px]">{o.description}</p>}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => openDetail(o)} className="text-[10px] bg-purple-700 text-white px-2 py-0.5 rounded font-medium">Details</button>
                          <button onClick={() => openHierarchy(o.object_id)} className="text-[10px] bg-slate-600 text-white px-2 py-0.5 rounded">Hierarchy</button>
                        </div>
                        {canEdit && <button onClick={() => deleteObj(o.object_id)} className="text-red-600 text-[10px] mt-1 underline">Delete</button>}
                      </div>
                    )}

                    {/* Customer popup */}
                    {o.obj_type === 'customer' && (
                      <div className="mt-1.5 space-y-0.5">
                        {o.house_no && <p className="text-[10px]">{o.house_no}, {o.street}</p>}
                        {o.township && <p className="text-[10px]">Township: {o.township}</p>}
                        {o.description && <p className="text-[10px]">{o.description}</p>}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => { setTracingId(o.object_id); setActiveTab('trace'); traceNetworkPath(o.object_id); }} className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded font-medium">Trace Path</button>
                          <button onClick={() => openHierarchy(o.object_id)} className="text-[10px] bg-slate-600 text-white px-2 py-0.5 rounded">Hierarchy</button>
                        </div>
                        {canEdit && <button onClick={() => deleteObj(o.object_id)} className="text-red-600 text-[10px] mt-1 underline">Delete</button>}
                      </div>
                    )}

                    {/* Potential popup */}
                    {o.obj_type === 'potential' && (
                      <div className="mt-1.5 space-y-0.5">
                        {o.estimated_houses && <p className="text-[10px]">Est. houses: <span className="font-bold">{o.estimated_houses}</span></p>}
                        {o.house_no && <p className="text-[10px]">{o.house_no}, {o.street}</p>}
                        {o.description && <p className="text-[10px]">{o.description}</p>}
                        {canEdit && <button onClick={() => deleteObj(o.object_id)} className="text-red-600 text-[10px] mt-1 underline">Delete</button>}
                      </div>
                    )}

                    <p className="text-[9px] text-gray-400 mt-1 tabular-nums">{o.lat?.toFixed(5)}, {o.lng?.toFixed(5)}</p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          </MarkerClusterGroup>

          {/* Click position marker */}
          {clickPos && !drawMode && (
            <Circle center={[clickPos.lat, clickPos.lng]} radius={5} color={distResult ? ZONE_COLORS[distResult.zone] : '#3B82F6'} fillOpacity={0.5} />
          )}

          {/* ODB coverage circles */}
          {layers.odb && objects.filter(o => o.obj_type === 'odb').map(o => (
            <Circle key={`cov-${o.object_id}`} center={[o.lat, o.lng]} radius={200} color="#0052CC" fillColor="#0052CC" fillOpacity={0.04} weight={1} dashArray="4 4" />
          ))}

          {/* DMA Customer Markers — clustered for performance */}
          {showDmaCustomers && (
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={50}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              iconCreateFunction={(cluster) => {
                const count = cluster.getChildCount();
                const bg = count < 10 ? '#22C55E' : count < 100 ? '#F59E0B' : count < 500 ? '#EF4444' : '#7C3AED';
                const size = 10;
                return L.divIcon({
                  html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:1.5px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3);"></div>`,
                  className: '',
                  iconSize: [size, size],
                  iconAnchor: [size/2, size/2]
                });
              }}
            >
              {dmaCustomers.filter(c => c.gpslat && c.gpslong && (dmaFilter === 'all' || (dmaFilter === 'active' && c.enableuser === 1) || (dmaFilter === 'expired' && c.enableuser !== 1))).map(c => (
                <Marker key={`dma-${c.username}`} position={[c.gpslat, c.gpslong]} zIndexOffset={highlightedCustomerId === c.username ? 1200 : 0} icon={highlightedCustomerId === c.username ? makeSearchHighlightIcon('customer') : dmaCustIcon(c.enableuser, c.expiration, c.last_session)}>
                  <Popup>
                    <div className="text-xs min-w-[200px]">
                      <p className="font-bold text-sm">{c.firstname} {c.lastname}</p>
                      <p className="text-[10px] text-gray-500 tabular-nums">{c.username}</p>
                      <div className="mt-1.5 space-y-0.5">
                        {(c.phone || c.mobile) && <p>Phone: <span className="font-medium">{c.phone || c.mobile}</span></p>}
                        {c.address && <p>Address: {c.address}</p>}
                        {c.service_name && <p>Package: <span className="font-medium">{c.service_name}</span></p>}
                        <p>Status: <span className={`font-bold ${c.enableuser === 1 ? 'text-green-700' : 'text-red-700'}`}>{(() => { const now = new Date(); const expDate = c.expiration && c.expiration !== '0000-00-00T00:00:00' ? new Date(c.expiration) : null; const isExpired = expDate && expDate <= now; if (c.enableuser !== 1 || isExpired) return '🔴 No Service'; const thirtyDaysAgo = new Date(now - 30*24*60*60*1000); const lastUse = c.last_session ? new Date(c.last_session) : null; if (!lastUse || lastUse < thirtyDaysAgo) return '⚫ Inactive (30d+)'; return '🟢 Active'; })()}</span></p>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <a href={`/customers/${c.username}`} className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded font-medium">Profile</a>
                        <button onClick={() => { setTracingId(c.username); setActiveTab('trace'); traceNetworkPath(c.username); }} className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded font-medium">Trace Path</button>
                      </div>
                      <p className="text-[9px] text-gray-400 mt-1 tabular-nums">{c.gpslat?.toFixed(5)}, {c.gpslong?.toFixed(5)}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}
          {/* ── Coverage Check Pin & Radius (INSIDE MapContainer) ─────────── */}
          {coverageMarker && (
            <Marker
              position={[coverageMarker.lat, coverageMarker.lng]}
              zIndexOffset={2000}
              icon={L.divIcon({
                className: '',
                html: `<div style="position:relative;width:40px;height:40px;">
                  <div class="coverage-pin-ring" style="position:absolute;top:0;left:0;width:40px;height:40px;border-radius:50%;background:rgba(239,68,68,0.35);"></div>
                  <div class="coverage-pin-pulse" style="position:absolute;top:8px;left:8px;width:24px;height:24px;border-radius:50%;background:#EF4444;border:3px solid white;box-shadow:0 2px 16px rgba(239,68,68,0.8);display:flex;align-items:center;justify-content:center;font-size:12px;">📍</div>
                </div>`,
                iconSize: [40, 40],
                iconAnchor: [20, 20],
                popupAnchor: [0, -20]
              })}
            >
              <Popup autoOpen={true}>
                <div style={{fontSize:'12px',minWidth:'160px',fontFamily:'sans-serif'}}>
                  <b style={{color:'#EF4444'}}>📍 Searched Location</b>
                  {coverageAddress && <div style={{fontSize:'10px',color:'#555',marginTop:'2px',maxWidth:'160px',wordBreak:'break-word'}}>{coverageAddress.substring(0,80)}</div>}
                  <div style={{marginTop:'4px',fontSize:'10px',color:'#888'}}>
                    {coverageMarker.lat.toFixed(5)}, {coverageMarker.lng.toFixed(5)}
                  </div>
                  {coverageResult && (
                    <div style={{marginTop:'6px',padding:'4px',background: coverageResult.covered ? '#f0fdf4' : '#fef2f2',borderRadius:'4px'}}>
                      <span style={{color: coverageResult.covered ? '#16a34a' : '#dc2626', fontWeight:'bold',fontSize:'11px'}}>
                        {coverageResult.covered ? '✅ Covered' : '❌ Not Covered'}
                      </span>
                      {coverageResult.nearest_odb && (
                        <div style={{marginTop:'3px',fontSize:'10px',color:'#444'}}>
                          <div>ODB: <b>{coverageResult.nearest_odb.name || coverageResult.nearest_odb}</b></div>
                          <div>Distance: <b>{coverageResult.distance_m}m</b></div>
                          <div>Free ports: <b>{coverageResult.available_ports}</b></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          )}
          {coverageMarker && (
            <Circle
              center={[coverageMarker.lat, coverageMarker.lng]}
              radius={typeof coverageRadius === 'number' ? coverageRadius : 300}
              pathOptions={{
                color: '#EF4444',
                fillColor: '#EF4444',
                fillOpacity: 0.08,
                weight: 2.5,
                dashArray: '8 5'
              }}
            />
          )}
          {/* Route polyline from searched location to ODB Box */}
          {routeData && routeData.coords && (
            <>
              <Polyline
                positions={routeData.coords}
                pathOptions={{
                  color: '#3B82F6',
                  weight: 6,
                  opacity: 0.85,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
                zIndexOffset={1500}
              >
                <Tooltip sticky>
                  <div style={{fontSize:'11px'}}>
                    <b>🧭 Route to {routeData.odbName}</b><br/>
                    📏 {routeData.distance < 1000 ? `${Math.round(routeData.distance)}m` : `${(routeData.distance/1000).toFixed(1)}km`} · ⏱ ~{Math.round(routeData.duration/60)} min ({routeData.mode})
                  </div>
                </Tooltip>
              </Polyline>
              {/* Route shadow line */}
              <Polyline
                positions={routeData.coords}
                pathOptions={{ color: '#1E40AF', weight: 10, opacity: 0.25 }}
              />
              {/* ODB destination marker */}
              <Marker
                position={[routeData.odbLat, routeData.odbLng]}
                zIndexOffset={3000}
                icon={L.divIcon({
                  className: '',
                  html: `<div style="position:relative;width:36px;height:36px;">
                    <div style="position:absolute;top:0;left:0;width:36px;height:36px;border-radius:50%;background:rgba(59,130,246,0.3);animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>
                    <div style="position:absolute;top:6px;left:6px;width:24px;height:24px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 2px 12px rgba(59,130,246,0.8);display:flex;align-items:center;justify-content:center;font-size:11px;">🏁</div>
                  </div>`,
                  iconSize: [36, 36],
                  iconAnchor: [18, 18],
                })}
              >
                <Popup>
                  <div style={{fontSize:'12px'}}>
                    <b style={{color:'#3B82F6'}}>🏁 {routeData.odbName}</b><br/>
                    <span style={{fontSize:'10px',color:'#555'}}>Destination ODB Box</span>
                  </div>
                </Popup>
              </Marker>
            </>
          )}
        </MapContainer>

        {/* ── Map Legend Panel ──────────────────────────────────────────────── */}
        <div className="absolute bottom-4 left-4 z-[1000]">
          <button
            onClick={() => setShowLegend(v => !v)}
            className="flex items-center gap-1.5 bg-[#0F172A]/90 backdrop-blur-sm border border-[#1E293B] rounded-xl px-3 py-1.5 shadow-xl text-[10px] font-bold text-white uppercase tracking-wide hover:bg-[#1E293B] transition-colors"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            Legend {showLegend ? '▲' : '▼'}
          </button>
          {showLegend && (
            <div className="mt-1 bg-[#0F172A]/90 backdrop-blur-sm border border-[#1E293B] rounded-xl p-3 shadow-xl min-w-[160px]">
              <p className="text-[10px] font-bold text-white uppercase tracking-wide mb-2">Map Legend</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div style={{width:'16px',height:'16px',borderRadius:'3px',background:'#BA1A1A',border:'2px solid white',boxShadow:'0 0 0 1px #F87171',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'5px',fontWeight:'bold'}}>OLT</div>
                  <span className="text-[10px] text-slate-300">OLT Location</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{width:'16px',height:'16px',borderRadius:'3px',background:'#16A34A',border:'2px solid white',boxShadow:'0 0 0 2px #4ADE80',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'5px',fontWeight:'900'}}>ODB</div>
                  <span className="text-[10px] text-slate-300">ODB Box (Online)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{width:'16px',height:'16px',borderRadius:'3px',background:'#DC2626',border:'2px solid white',boxShadow:'0 0 0 2px #F87171',display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontSize:'5px',fontWeight:'900'}}>ODB</div>
                  <span className="text-[10px] text-slate-300">ODB Box (Offline)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{width:'14px',height:'14px',background:'#EAB308',border:'1.5px solid white',transform:'rotate(45deg)'}}></div>
                  <span className="text-[10px] text-slate-300">Splitter</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#2563EB" stroke="white" strokeWidth="1.5"><path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"/></svg>
                  <span className="text-[10px] text-slate-300">Customer (Active)</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#6B7280" stroke="white" strokeWidth="1.5"><path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"/></svg>
                  <span className="text-[10px] text-slate-300">Customer (Inactive)</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#F97316" stroke="white" strokeWidth="1.5"><path d="M3 12L12 3L21 12V21H15V15H9V21H3V12Z"/></svg>
                  <span className="text-[10px] text-slate-300">Customer (Expired)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{width:'10px',height:'10px',borderRadius:'50%',background:'#F57F17',border:'1.5px solid white'}}></div>
                  <span className="text-[10px] text-slate-300">Potential Customer</span>
                </div>
                <div className="mt-1.5 pt-1.5 border-t border-[#1E293B]">
                  <p className="text-[9px] font-bold text-slate-400 mb-1">Cables</p>
                  <div className="flex items-center gap-2"><div style={{width:'20px',height:'3px',background:'#EF4444',borderRadius:'2px'}}></div><span className="text-[9px] text-slate-400">Backbone</span></div>
                  <div className="flex items-center gap-2 mt-0.5"><div style={{width:'20px',height:'3px',background:'#3B82F6',borderRadius:'2px'}}></div><span className="text-[9px] text-slate-400">Distribution</span></div>
                  <div className="flex items-center gap-2 mt-0.5"><div style={{width:'20px',height:'3px',background:'#22C55E',borderRadius:'2px'}}></div><span className="text-[9px] text-slate-400">Drop Cable</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Draw mode indicator */}
        {drawMode && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-[#0F172A]/95 border border-yellow-500 rounded-xl px-4 py-2 shadow-xl">
            <p className="text-[11px] font-bold text-yellow-400">
              ✏ Drawing: {drawMode === 'backbone_route' ? '🔴 Backbone' : drawMode === 'distribution_route' ? '🔵 Distribution' : drawMode === 'drop_cable' ? '🟢 Drop Cable' : '⬡ Coverage Zone'}
            </p>
            <p className="text-[9px] text-slate-400">{drawPoints.length} points — Click map to add • Use panel to save</p>
          </div>
        )}
      </div>

      {/* ── Add Object Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md bg-[#0F172A] text-white rounded-2xl z-[2000] border border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">Place Network Object</DialogTitle></DialogHeader>
          <form onSubmit={saveObject} className="space-y-3 mt-2">
            {clickPos && <p className="text-[11px] bg-[#1E293B] p-2 rounded-lg tabular-nums text-slate-300">Location: {clickPos.lat.toFixed(5)}, {clickPos.lng.toFixed(5)}</p>}
            {!clickPos && <p className="text-[11px] text-yellow-400 bg-yellow-900/30 p-2 rounded-lg">Click on the map first to set location</p>}
            <div>
              <label className="text-[11px] font-medium mb-1 block text-slate-300">Object Type</label>
              <Select value={addType} onValueChange={setAddType}>
                <SelectTrigger className="bg-[#1E293B] border-[#334155] rounded-xl h-10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-[#334155] z-[3000]">
                  <SelectItem value="olt">OLT Location</SelectItem>
                  <SelectItem value="splitter">Splitter</SelectItem>
                  <SelectItem value="odb">ODB Box</SelectItem>
                  <SelectItem value="customer">Customer House</SelectItem>
                  <SelectItem value="potential">Potential Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-medium mb-1 block text-slate-300">Name</label>
              <input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} required className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" />
            </div>
            {addType === 'olt' && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Port Label (e.g. 0/16)</label><input value={addForm.port_label} onChange={e => setAddForm({ ...addForm, port_label: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Alarm Status</label>
                  <select value={addForm.alarm_status} onChange={e => setAddForm({ ...addForm, alarm_status: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white">
                    <option>Normal</option><option>Warning</option><option>Critical</option>
                  </select>
                </div>
              </div>
            )}
            {addType === 'splitter' && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Ratio</label>
                  <select value={addForm.ratio} onChange={e => setAddForm({ ...addForm, ratio: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white">
                    <option>1:4</option><option>1:8</option><option>1:16</option><option>1:32</option><option>1:64</option>
                  </select>
                </div>
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Parent OLT/Splitter ID</label><input value={addForm.parent_id} onChange={e => setAddForm({ ...addForm, parent_id: e.target.value })} placeholder="Optional" className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
              </div>
            )}
            {addType === 'odb' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Capacity (ports)</label><input type="number" value={addForm.capacity} onChange={e => setAddForm({ ...addForm, capacity: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white tabular-nums" /></div>
                  <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Used Ports</label><input type="number" value={addForm.used_ports} onChange={e => setAddForm({ ...addForm, used_ports: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white tabular-nums" /></div>
                </div>
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Parent Splitter ID</label><input value={addForm.parent_id} onChange={e => setAddForm({ ...addForm, parent_id: e.target.value })} placeholder="Optional" className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Township</label><input value={addForm.township} onChange={e => setAddForm({ ...addForm, township: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
                  <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Street</label><input value={addForm.street} onChange={e => setAddForm({ ...addForm, street: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
                </div>
              </>
            )}
            {(addType === 'customer' || addType === 'potential') && (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">House No</label><input value={addForm.house_no} onChange={e => setAddForm({ ...addForm, house_no: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
                <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Street</label><input value={addForm.street} onChange={e => setAddForm({ ...addForm, street: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
              </div>
            )}
            {addType === 'potential' && (
              <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Estimated Houses</label><input type="number" value={addForm.estimated_houses} onChange={e => setAddForm({ ...addForm, estimated_houses: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white tabular-nums" /></div>
            )}
            <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Description</label><input value={addForm.description} onChange={e => setAddForm({ ...addForm, description: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setAddOpen(false)} className="flex-1 bg-[#1E293B] text-slate-300 py-2 rounded-xl text-sm border border-[#334155]">Cancel</button>
              <button type="submit" disabled={!clickPos} className="flex-1 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white py-2 rounded-xl text-sm font-medium">Place on Map</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Cable Edit Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={!!cableEditOpen} onOpenChange={() => setCableEditOpen(null)}>
        <DialogContent className="sm:max-w-sm bg-[#0F172A] text-white rounded-2xl z-[2000] border border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">Edit Cable Properties</DialogTitle></DialogHeader>
          {cableEditOpen && (
            <form onSubmit={saveCableEdit} className="space-y-3 mt-2">
              <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Cable Name</label><input value={cableEditForm.name} onChange={e => setCableEditForm({ ...cableEditForm, name: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white" /></div>
              <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Status</label>
                <select value={cableEditForm.cable_status} onChange={e => setCableEditForm({ ...cableEditForm, cable_status: e.target.value })} className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white">
                  <option value="Active">🟢 Active</option>
                  <option value="Cut">🔴 Cut</option>
                  <option value="Degraded">🟠 Degraded</option>
                </select>
              </div>
              <div><label className="text-[11px] font-medium mb-1 block text-slate-300">Length (meters)</label><input type="number" value={cableEditForm.length_meters} onChange={e => setCableEditForm({ ...cableEditForm, length_meters: e.target.value })} placeholder="Auto-calculated" className="w-full bg-[#1E293B] border border-[#334155] rounded-xl px-3 py-2 text-sm text-white tabular-nums" /></div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setCableEditOpen(null)} className="flex-1 bg-[#1E293B] text-slate-300 py-2 rounded-xl text-sm border border-[#334155]">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-700 hover:bg-blue-600 text-white py-2 rounded-xl text-sm font-medium">Save</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Network Hierarchy Dialog ──────────────────────────────────────────── */}
      <Dialog open={hierarchyOpen} onOpenChange={setHierarchyOpen}>
        <DialogContent className="sm:max-w-md bg-[#0F172A] text-white rounded-2xl z-[2000] border border-[#1E293B]">
          <DialogHeader><DialogTitle className="text-white">Network Hierarchy</DialogTitle></DialogHeader>
          {hierarchyData && (
            <div className="space-y-3 mt-2">
              <p className="text-[11px] text-slate-400">Full path from OLT to this element:</p>
              <div className="space-y-1.5">
                {(hierarchyData.hierarchy || []).map((h, i) => (
                  <div key={h.object_id || i} className="flex items-center gap-2 p-2 bg-[#1E293B] rounded-lg">
                    <div className="w-6 h-6 rounded flex items-center justify-center text-[8px] font-bold flex-shrink-0" style={{ background: h.obj_type === 'olt' ? '#BA1A1A' : h.obj_type === 'splitter' ? '#7C4DFF' : h.obj_type === 'odb' ? '#0052CC' : '#006D3B' }}>
                      {h.obj_type === 'olt' ? 'OLT' : h.obj_type === 'splitter' ? 'SP' : h.obj_type === 'odb' ? 'ODB' : 'C'}
                    </div>
                    <div className="flex-1">
                      <p className="text-[11px] text-white font-medium">{h.name || h.object_id}</p>
                      <p className="text-[9px] text-slate-500 uppercase">{h.obj_type}</p>
                    </div>
                    {h.port_label && <span className="text-[9px] text-slate-400 bg-[#334155] px-1.5 py-0.5 rounded">{h.port_label}</span>}
                    {i < (hierarchyData.hierarchy?.length || 0) - 1 && <span className="text-slate-600 text-xs">→</span>}
                  </div>
                ))}
              </div>
              {hierarchyData.children_count > 0 && (
                <div className="border-t border-[#1E293B] pt-2">
                  <p className="text-[10px] text-slate-400">Downstream: <span className="font-bold text-white">{hierarchyData.children_count}</span> connected elements</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── OLT/Splitter Detail Dialog ────────────────────────────────────────── */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-lg bg-[#0F172A] text-white rounded-2xl z-[2000] border border-[#1E293B] max-h-[80vh] overflow-y-auto">
          {detailData && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold" style={{ background: detailData.type === 'olt' ? '#BA1A1A' : '#7C4DFF' }}>
                    {detailData.type === 'olt' ? 'OLT' : 'SP'}
                  </div>
                  {detailData.olt_name || detailData.splitter_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div className="flex items-center gap-2">
                  <AlarmBadge status={detailData.alarm_status} />
                  {detailData.ratio && <span className="text-[10px] bg-purple-900 text-purple-200 px-1.5 py-0.5 rounded">Ratio: {detailData.ratio}</span>}
                </div>
                {detailData.type === 'olt' && (
                  <>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-[#1E293B] rounded-lg p-2 text-center"><p className="text-[14px] font-bold text-purple-400">{detailData.splitter_count || 0}</p><p className="text-[8px] text-slate-500">Splitters</p></div>
                      <div className="bg-[#1E293B] rounded-lg p-2 text-center"><p className="text-[14px] font-bold text-blue-400">{detailData.odb_count || 0}</p><p className="text-[8px] text-slate-500">ODB Boxes</p></div>
                      <div className="bg-[#1E293B] rounded-lg p-2 text-center"><p className="text-[14px] font-bold text-green-400">{detailData.utilization_pct || 0}%</p><p className="text-[8px] text-slate-500">Utilization</p></div>
                    </div>
                    <PortBar used={detailData.used_ports || 0} total={detailData.total_ports || 0} label="Total Port Usage" />
                  </>
                )}
                {detailData.type === 'splitter' && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#1E293B] rounded-lg p-2 text-center"><p className="text-[14px] font-bold text-blue-400">{detailData.odb_count || 0}</p><p className="text-[8px] text-slate-500">ODB Boxes</p></div>
                      <div className="bg-[#1E293B] rounded-lg p-2 text-center"><p className="text-[14px] font-bold text-green-400">{detailData.used_ports || 0}/{detailData.total_ports || 0}</p><p className="text-[8px] text-slate-500">Ports Used</p></div>
                    </div>
                    {detailData.parent && <p className="text-[10px] text-slate-400">Parent: <span className="text-white font-medium">{detailData.parent.name}</span> ({detailData.parent.obj_type})</p>}
                  </>
                )}
                {/* Downstream ODBs */}
                {(detailData.downstream_odbs || detailData.odbs || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 mb-1.5">Downstream ODB Boxes</p>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {(detailData.downstream_odbs || detailData.odbs || []).map(o => (
                        <div key={o.object_id} className="flex items-center gap-2 p-1.5 bg-[#1E293B] rounded-lg">
                          <div className="w-4 h-4 rounded bg-blue-800 flex items-center justify-center text-[7px] text-white font-bold flex-shrink-0">ODB</div>
                          <span className="text-[10px] text-slate-300 flex-1 truncate">{o.name}</span>
                          <span className="text-[9px] text-slate-500 tabular-nums">{o.used_ports || 0}/{o.capacity || 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── ODB Port Management Dialog ────────────────────────────────────────── */}
      <Dialog open={!!portOpen} onOpenChange={() => setPortOpen(null)}>
        <DialogContent className="sm:max-w-2xl bg-white rounded-2xl z-[2000] max-h-[90vh] overflow-y-auto">
          {portOpen && portData && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-blue-700 flex items-center justify-center text-[8px] text-white font-bold">ODB</div>
                  {portData.odb_name || portOpen.name} — Port Management
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <div className="flex-1">
                    <div className="flex justify-between text-[12px] mb-1">
                      <span>Ports Used: <span className="font-bold">{portData.used}</span>/{portData.capacity}</span>
                      <span className="font-bold text-blue-600">{portData.available} available</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                      <div className="h-2.5 rounded-full transition-all" style={{ width: `${(portData.used / portData.capacity) * 100}%`, background: portData.available <= 2 ? '#EF4444' : portData.available <= 5 ? '#F59E0B' : '#3B82F6' }} />
                    </div>
                  </div>
                </div>
                {/* Port grid */}
                <div>
                  <p className="text-[12px] font-medium mb-2">Port Assignments</p>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                    {Array.from({ length: portData.capacity }, (_, i) => {
                      const port = portData.assignments.find(a => a.port_number === i + 1);
                      return (
                        <div key={i} className={`relative p-1.5 rounded-lg text-center text-[10px] font-bold cursor-default border ${port ? (port.status === 'Active' ? 'bg-green-50 border-green-400 text-green-700' : 'bg-red-50 border-red-300 text-red-600') : 'bg-slate-50 border-slate-200 text-slate-400'}`} title={port ? `${port.customer_name} (${port.customer_id})` : `Port ${i + 1} - Available`}>
                          <span>{i + 1}</span>
                          {port && <div className="w-1.5 h-1.5 rounded-full mx-auto mt-0.5" style={{ background: port.status === 'Active' ? '#22C55E' : '#EF4444' }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Assignment table */}
                {portData.assignments.length > 0 && (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left text-[10px] font-medium text-slate-500 px-3 py-2">Port</th>
                          <th className="text-left text-[10px] font-medium text-slate-500 px-3 py-2">Customer</th>
                          <th className="text-left text-[10px] font-medium text-slate-500 px-3 py-2 hidden sm:table-cell">Plan</th>
                          <th className="text-left text-[10px] font-medium text-slate-500 px-3 py-2 hidden sm:table-cell">ONU Serial</th>
                          <th className="text-left text-[10px] font-medium text-slate-500 px-3 py-2">Status</th>
                          <th className="text-right text-[10px] font-medium text-slate-500 px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portData.assignments.map(a => (
                          <tr key={a.assignment_id} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-sm font-bold tabular-nums text-blue-600">{a.port_number}</td>
                            <td className="px-3 py-2"><p className="text-xs font-medium">{a.customer_name}</p><p className="text-[10px] text-slate-400">{a.customer_id}</p></td>
                            <td className="px-3 py-2 text-[11px] hidden sm:table-cell">{a.service_plan}</td>
                            <td className="px-3 py-2 text-[10px] tabular-nums hidden sm:table-cell">{a.onu_serial}</td>
                            <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${a.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{a.status}</span></td>
                            <td className="px-3 py-2 text-right">{a.status === 'Active' && <button onClick={() => disconnectPort(a.assignment_id)} className="text-[10px] text-red-600 underline">Disconnect</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Assign new port form */}
                {portData.available > 0 && (
                  <div className="border border-slate-200 rounded-xl p-4">
                    <p className="text-[13px] font-medium mb-3">Assign New Port</p>
                    <form onSubmit={assignPort} className="space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div><label className="text-[10px] font-medium mb-1 block">Port #</label><input type="number" min="1" max={portData.capacity} value={portForm.port_number} onChange={e => setPortForm({ ...portForm, port_number: e.target.value })} placeholder="Auto" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                        <div><label className="text-[10px] font-medium mb-1 block">Customer ID (PPPoE)</label><input value={portForm.customer_id} onChange={e => setPortForm({ ...portForm, customer_id: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                        <div><label className="text-[10px] font-medium mb-1 block">Customer Name</label><input value={portForm.customer_name} onChange={e => setPortForm({ ...portForm, customer_name: e.target.value })} required className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><label className="text-[10px] font-medium mb-1 block">Phone</label><input value={portForm.customer_phone} onChange={e => setPortForm({ ...portForm, customer_phone: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                        <div><label className="text-[10px] font-medium mb-1 block">Service Plan</label><input value={portForm.service_plan} onChange={e => setPortForm({ ...portForm, service_plan: e.target.value })} placeholder="ML_25M" className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                        <div><label className="text-[10px] font-medium mb-1 block">ONU Serial</label><input value={portForm.onu_serial} onChange={e => setPortForm({ ...portForm, onu_serial: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm tabular-nums" /></div>
                      </div>
                      <div><label className="text-[10px] font-medium mb-1 block">Address</label><input value={portForm.customer_address} onChange={e => setPortForm({ ...portForm, customer_address: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm" /></div>
                      <button type="submit" className="w-full bg-blue-700 hover:bg-blue-600 text-white text-xs py-2.5 rounded-xl font-medium">Assign Port</button>
                    </form>
                  </div>
                )}
                {portData.available === 0 && <div className="p-3 bg-red-50 rounded-xl text-[12px] text-red-700 font-medium text-center">ODB is full — No available ports</div>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
